/**
 * DeskTab — observation monitoring surface
 *
 * Component hierarchy:
 *   DeskTab
 *     └─ DeskObservationsTab
 *          └─ ObservationCard  ← container; inner-agnostic; slot via children
 *               └─ NgDecayInner  ← NG-specific; plugs in as children
 *                    ├─ DecayChart           (SVG, gap breaks at null premiums)
 *                    └─ LockedContractsTable (structure | strike | lock | now | Δ)
 *
 * Pending backend endpoint:
 *   GET /api/desk/observations — observation list (DeskObservationsTab hardcodes id=1 until built)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getDeskStatus, getObservationSamples, getObservationInstruments, getObservationLive, getNgCandles, getNgIndicators, getObservationList, getNiftyObsLive } from '../../api/desk';
import { Ticker } from '../ui';
import PaperTradingTab from './PaperTradingTab';
import UpstoxTab from './UpstoxTab';

// Hardcoded until GET /api/desk/observations list endpoint exists
const NG_OBS_ID = 1;

const C_STRADDLE       = '#2a78d6';
const C_STRANGLE       = '#1baf7a';
const C_STRADDLE_FADED = '#b0aead';
const C_STRANGLE_FADED = '#cdc9c0';

// ── Date helpers ──────────────────────────────────────────────────────────────

// SQLite datetime() returns UTC without 'Z'. Append 'Z' so JS treats it as UTC.
function utcDate(isoStr) {
  if (!isoStr) return null;
  const s = /[Z+]/.test(isoStr) ? isoStr : isoStr + 'Z';
  return new Date(s);
}

function fmtTimeIST(isoStr) {
  const d = utcDate(isoStr);
  if (!d) return '—';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Asia/Kolkata',
  });
}

function fmtAge(isoStr) {
  const d = utcDate(isoStr);
  if (!d) return null;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60)   return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  const h = Math.floor(sec / 3600);
  return `${h}h ${Math.floor((sec % 3600) / 60)}m ago`;
}

function fmt2(v) {
  return v == null ? '—' : Number(v).toFixed(2);
}

// ── Role label parser ─────────────────────────────────────────────────────────

// "set1 straddle 305CE" → { setNum:1, structure:'straddle', contract:'305CE', isCall:true, isPut:false }
function parseRole(role) {
  if (!role) return null;
  const m = role.match(/^set(\d+)\s+(straddle|strangle)\s+(.+)$/);
  if (!m) return null;
  const contract = m[3];
  return {
    setNum:    parseInt(m[1], 10),
    structure: m[2],
    contract,
    isCall: contract.endsWith('CE'),
    isPut:  contract.endsWith('PE'),
  };
}

// ── Premium series builder ────────────────────────────────────────────────────

/**
 * Given the flat sample rows (newest-first from API), build per-(setNum,structure)
 * time series of premium = CE.ltp + PE.ltp, with null where either leg is gapped/null.
 *
 * Returns:
 *   series: { [setNum]: { straddle: [{t,v}], strangle: [{t,v}] } }
 *   maxSetNum: number
 *   times: string[] ascending ISO
 */
function buildPremiumSeries(rows) {
  if (!rows || !rows.length) return { series: {}, maxSetNum: 0, times: [] };

  // Build tick map: captured_at → { role_label → { ltp, gap } }
  const tickMap = {};
  const setNums = new Set();
  for (const r of rows) {
    if (!tickMap[r.captured_at]) tickMap[r.captured_at] = {};
    tickMap[r.captured_at][r.role_label] = { ltp: r.ltp, gap: r.gap_reason };
    const p = parseRole(r.role_label);
    if (p) setNums.add(p.setNum);
  }

  const times = Object.keys(tickMap).sort();  // ascending
  const series = {};

  for (const setNum of [...setNums].sort((a, b) => a - b)) {
    series[setNum] = { straddle: [], strangle: [] };

    for (const t of times) {
      const tick = tickMap[t];

      // For each structure, find CE+PE legs for this setNum
      for (const structure of ['straddle', 'strangle']) {
        let ceData = null, peData = null;
        for (const [role, data] of Object.entries(tick)) {
          const p = parseRole(role);
          if (!p || p.setNum !== setNum || p.structure !== structure) continue;
          if (p.isCall) ceData = data;
          if (p.isPut)  peData = data;
        }

        const good = ceData && peData &&
                     !ceData.gap && !peData.gap &&
                     ceData.ltp != null && peData.ltp != null;

        series[setNum][structure].push({
          t,
          v: good ? ceData.ltp + peData.ltp : null,
        });
      }
    }
  }

  const maxSetNum = setNums.size ? Math.max(...setNums) : 0;
  return { series, maxSetNum, times };
}

// Split a [{t,v}] array into non-null run segments (for gap-break rendering)
function toSegments(pts) {
  const segs = [];
  let cur = [];
  for (const p of pts) {
    if (p.v == null) {
      if (cur.length) { segs.push(cur); cur = []; }
    } else {
      cur.push(p);
    }
  }
  if (cur.length) segs.push(cur);
  return segs;
}

// ── Next-sample line ──────────────────────────────────────────────────────────
// Market-aware: only shows a countdown when the market will actually be live.
// Uses desk_status state so it agrees with what the sampler is doing.
// Returns { text, dim } — dim=true for closed/disconnected states.
function computeNextSample(deskStatus, lastFiredAt, intervalSeconds) {
  if (!lastFiredAt || !intervalSeconds) return null;

  if (!deskStatus || deskStatus === 'NOT_CONNECTED' || deskStatus === 'TOKEN_EXPIRED') {
    return { text: 'not connected', dim: true };
  }
  if (deskStatus === 'DISCONNECTED' || deskStatus === 'TICKER_DOWN') {
    return { text: 'not connected', dim: true };
  }
  if (deskStatus === 'MARKET_CLOSED') {
    // Don't imply a sample will fire when the market is shut
    return { text: 'market closed — resumes next session', dim: true };
  }

  // Live / stale / awaiting — compute expected time
  const lastMs = utcDate(lastFiredAt)?.getTime();
  if (!lastMs) return null;

  const nextMs = lastMs + intervalSeconds * 1000;
  const nowMs  = Date.now();

  if (nextMs <= nowMs) {
    // Overdue: sampler is due but hasn't fired yet — either about to fire or stuck
    return { text: 'next sample any moment', dim: false };
  }

  const timeStr = new Date(nextMs).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Asia/Kolkata',
  });
  return { text: `next ~${timeStr} IST`, dim: false };
}

// ── State chip ────────────────────────────────────────────────────────────────

const STATE_META = {
  LIVE:                { label: 'Live',           dotColor: '#2CA253', textColor: '#1d7a3c', bg: 'rgba(45,138,80,.10)'  },
  STALE:               { label: 'Stale',          dotColor: '#c8922a', textColor: '#a06010', bg: 'rgba(200,146,42,.12)' },
  MARKET_CLOSED:       { label: 'Market Closed',  dotColor: 'var(--ink-4)', textColor: 'var(--ink-3)', bg: 'var(--surface-2)' },
  DISCONNECTED:        { label: 'Disconnected',   dotColor: '#b85555', textColor: '#8f3e3e', bg: 'rgba(168,93,87,.10)' },
  NOT_CONNECTED:       { label: 'Not Connected',  dotColor: 'var(--ink-4)', textColor: 'var(--ink-3)', bg: 'var(--surface-2)' },
  AWAITING_FIRST_TICK: { label: 'Awaiting Tick',  dotColor: '#6470b8', textColor: '#4a5498', bg: 'rgba(80,96,160,.10)' },
  // New states from permanent-fix branch
  TICKER_DOWN:         { label: 'Ticker Down',    dotColor: '#b85555', textColor: '#8f3e3e', bg: 'rgba(168,93,87,.18)' },
  TOKEN_EXPIRED:       { label: 'Token Expired',  dotColor: '#c8922a', textColor: '#a06010', bg: 'rgba(200,146,42,.12)' },
};

function StatusDot({ state, size = 7 }) {
  const m = STATE_META[state] || STATE_META.NOT_CONNECTED;
  return (
    <span style={{
      display: 'inline-block', flexShrink: 0,
      width: size, height: size,
      borderRadius: '50%', background: m.dotColor,
    }}/>
  );
}

function StateChip({ state }) {
  const m = STATE_META[state] || STATE_META.NOT_CONNECTED;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 9.5, fontWeight: 700, letterSpacing: '.055em',
      textTransform: 'uppercase',
      padding: '2px 6px', borderRadius: 'var(--r-2)',
      color: m.textColor, background: m.bg, flexShrink: 0,
    }}>
      <StatusDot state={state} size={5}/>
      {m.label}
    </span>
  );
}

// ── Decay chart (SVG, gap breaks) ─────────────────────────────────────────────
// IST = UTC+5:30 = +19800 s. All IST helpers use this offset for reliability.
const IST_OFFSET_MS = 19800 * 1000;
const istDayStr = (ms) => {
  const d = new Date(ms + IST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
};
const istSecOfDay = (ms) => {
  const d = new Date(ms + IST_OFFSET_MS);
  return d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds();
};
const fmtDayLabel = (ymd) => {
  const [,m,d] = ymd.split('-').map(Number);
  const MON = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d).padStart(2,'0')} ${MON[m]}`;
};
// All weekday (Mon–Fri) dates in [startYmd, endYmd] inclusive, YYYY-MM-DD
const weekdaysBetween = (startYmd, endYmd) => {
  const days = [];
  let ms = new Date(startYmd + 'T00:00:00Z').getTime();
  const endMs = new Date(endYmd + 'T00:00:00Z').getTime();
  while (ms <= endMs) {
    const dow = new Date(ms).getUTCDay(); // UTC day — safe because dates are midnight UTC
    if (dow !== 0 && dow !== 6) days.push(istDayStr(ms - IST_OFFSET_MS + 86400000/2)); // midday UTC → correct IST date
    ms += 86400000;
  }
  return days;
};

function DecayChart({ series, maxSetNum }) {
  // MCX NG session: 09:00–23:30 IST = 32400–84600 seconds from midnight IST
  const SESSION_START = 9 * 3600;
  const SESSION_END   = 23.5 * 3600;
  const SESSION_DUR   = SESSION_END - SESSION_START;

  const PAD = { t: 10, r: 14, b: 42, l: 42 }; // extra bottom for 2-line x-axis
  const VW = 600, VH = 195;
  const innerW = VW - PAD.l - PAD.r;
  const innerH = VH - PAD.t - PAD.b;

  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const allNonNull = useMemo(() => {
    const pts = [];
    for (const [sn, s] of Object.entries(series)) {
      pts.push(...s.straddle.filter(p => p.v != null).map(p => ({ ...p, kind: 'straddle', setNum: Number(sn) })));
      pts.push(...s.strangle.filter(p => p.v != null).map(p => ({ ...p, kind: 'strangle', setNum: Number(sn) })));
    }
    return pts;
  }, [series]);

  if (!allNonNull.length) {
    return (
      <div style={{
        height: VH, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-4)', fontSize: 11,
      }}>
        No live data yet
      </div>
    );
  }

  // ── Day-indexed x-axis ────────────────────────────────────────────────────
  // Each trading weekday (Mon–Fri) gets an equal horizontal slot. Days with no
  // data appear as blank gaps — intentional, shows when recording was off.
  // Within each day's slot, points are placed proportionally by IST time of day.
  const tMin = Math.min(...allNonNull.map(p => utcDate(p.t).getTime()));
  const tMax = Math.max(...allNonNull.map(p => utcDate(p.t).getTime()));

  const startYmd = istDayStr(tMin);
  const endYmd   = istDayStr(tMax);
  const tradingDays = weekdaysBetween(startYmd, endYmd); // Mon–Fri inclusive
  const nDays = tradingDays.length || 1;
  const dayW  = innerW / nDays;

  const xOf = (isoStr) => {
    const ms     = utcDate(isoStr).getTime();
    const dStr   = istDayStr(ms);
    const dayIdx = tradingDays.indexOf(dStr);
    if (dayIdx < 0) return PAD.l;
    const sec    = istSecOfDay(ms);
    const frac   = Math.max(0, Math.min(1, (sec - SESSION_START) / SESSION_DUR));
    return PAD.l + dayIdx * dayW + frac * dayW;
  };

  const vMax   = Math.max(...allNonNull.map(p => p.v)) * 1.18;
  const yOf    = (v) => PAD.t + (1 - v / vMax) * innerH;

  // Horizontal grid lines
  const gridSteps = 4;
  const gridVals  = Array.from({ length: gridSteps + 1 }, (_, i) => (vMax / gridSteps) * i);

  // X-axis: one tick per trading day, label at slot centre
  const dayTicks = tradingDays.map((ymd, i) => ({
    x:     PAD.l + (i + 0.5) * dayW,
    xLeft: PAD.l + i * dayW,
    label: fmtDayLabel(ymd),
    ymd,
  }));

  const setNums = Object.keys(series).map(Number).sort((a, b) => a - b);

  // ── Mouse: use SVG coordinate transform (fixes viewBox/container mismatch) ─
  const handleMouseMove = (e) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    // createSVGPoint accounts for preserveAspectRatio letterboxing correctly
    const pt = svgEl.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svgEl.getScreenCTM().inverse());
    const svgX  = svgPt.x;

    if (svgX < PAD.l || svgX > VW - PAD.r) { setTooltip(null); return; }

    let best = null, bestDist = Infinity;
    for (const p of allNonNull) {
      const d = Math.abs(xOf(p.t) - svgX);
      if (d < bestDist) { bestDist = d; best = p; }
    }
    if (!best || bestDist > 20) { setTooltip(null); return; }

    const snapT      = best.t;
    const snapSetNum = best.setNum;
    const straddlePt = series[snapSetNum]?.straddle.find(p => p.t === snapT);
    const stranglePt = series[snapSetNum]?.strangle.find(p => p.t === snapT);
    const snapX      = xOf(snapT);
    const tipOnLeft  = snapX > VW * 0.62;

    const ms = utcDate(snapT).getTime();
    const [hh, mm, ss] = [
      Math.floor(istSecOfDay(ms) / 3600),
      Math.floor((istSecOfDay(ms) % 3600) / 60),
      istSecOfDay(ms) % 60,
    ].map(n => String(n).padStart(2, '0'));
    const timeStr = `${hh}:${mm}:${ss} IST`;
    const dateStr = fmtDayLabel(istDayStr(ms));

    setTooltip({
      svgX: snapX,
      tipOnLeft,
      setNum: snapSetNum,
      date: dateStr,
      time: timeStr,
      straddle: straddlePt?.v ?? null,
      strangle: stranglePt?.v ?? null,
    });
  };

  const renderSeries = (pts, color, strokeW, dashArray) => {
    const segs = toSegments(pts);
    return segs.map((seg, i) => {
      if (seg.length === 1) {
        return <circle key={i} cx={xOf(seg[0].t)} cy={yOf(seg[0].v)} r={2.5} fill={color}/>;
      }
      const polyPoints = seg.map(p => `${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' ');
      return (
        <polyline key={i} points={polyPoints}
          fill="none" stroke={color} strokeWidth={strokeW}
          strokeLinejoin="round" strokeLinecap="round"
          strokeDasharray={dashArray || undefined}/>
      );
    });
  };

  return (
    <div style={{ position: 'relative', userSelect: 'none' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width: '100%', height: VH, display: 'block', cursor: 'crosshair' }}
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* Horizontal grid lines */}
        {gridVals.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={yOf(v)} x2={VW - PAD.r} y2={yOf(v)}
              stroke="var(--hair)" strokeWidth={0.75}/>
            <text x={PAD.l - 6} y={yOf(v) + 3.5}
              textAnchor="end" fontSize={9} fill="var(--ink-3)">
              {v.toFixed(0)}
            </text>
          </g>
        ))}

        {/* X-axis baseline */}
        <line x1={PAD.l} y1={VH - PAD.b} x2={VW - PAD.r} y2={VH - PAD.b}
          stroke="var(--hair-strong)" strokeWidth={0.75}/>

        {/* Day boundary lines + date labels */}
        {dayTicks.map(({ x, xLeft, label }, i) => (
          <g key={label}>
            {/* Faint vertical boundary at left edge of each day slot */}
            {i > 0 && (
              <line x1={xLeft} y1={PAD.t} x2={xLeft} y2={VH - PAD.b}
                stroke="var(--hair)" strokeWidth={0.5} strokeDasharray="2 3"/>
            )}
            {/* Date label centred in the slot */}
            <text x={x} y={VH - PAD.b + 13}
              textAnchor="middle" fontSize={9} fill="var(--ink-3)">
              {label}
            </text>
          </g>
        ))}

        {/* Series — old sets drawn first (underneath) */}
        {setNums.map(setNum => {
          const isActive = setNum === maxSetNum;
          const opacity  = isActive ? 1 : 0.5;
          const sw       = isActive ? 1.75 : 1;
          return (
            <g key={setNum} opacity={opacity}>
              {renderSeries(series[setNum].straddle, isActive ? C_STRADDLE : C_STRADDLE_FADED, sw, null)}
              {renderSeries(series[setNum].strangle, isActive ? C_STRANGLE : C_STRANGLE_FADED, sw, isActive ? null : '4 2')}
            </g>
          );
        })}

        {/* Crosshair + snap dots */}
        {tooltip && (
          <line x1={tooltip.svgX} y1={PAD.t} x2={tooltip.svgX} y2={VH - PAD.b}
            stroke="var(--ink-3)" strokeWidth={0.75} strokeDasharray="3 2" pointerEvents="none"/>
        )}
        {tooltip && tooltip.straddle != null && (
          <circle cx={tooltip.svgX} cy={yOf(tooltip.straddle)} r={3.5}
            fill={C_STRADDLE} stroke="var(--surface-1)" strokeWidth={1.5} pointerEvents="none"/>
        )}
        {tooltip && tooltip.strangle != null && (
          <circle cx={tooltip.svgX} cy={yOf(tooltip.strangle)} r={3.5}
            fill={C_STRANGLE} stroke="var(--surface-1)" strokeWidth={1.5} pointerEvents="none"/>
        )}
      </svg>

      {/* Floating tooltip */}
      {tooltip && (
        <div style={{
          position: 'absolute', top: 8,
          left:  tooltip.tipOnLeft ? undefined : `calc(${(tooltip.svgX / VW) * 100}% + 8px)`,
          right: tooltip.tipOnLeft ? `calc(${100 - (tooltip.svgX / VW) * 100}% + 8px)` : undefined,
          pointerEvents: 'none',
          background: 'var(--surface-1)', border: '.5px solid var(--hair-strong)',
          borderRadius: 'var(--r-2)', padding: '5px 8px',
          fontSize: 10, lineHeight: 1.65, whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,.12)', zIndex: 10,
        }}>
          <div style={{ color: 'var(--ink-2)', fontWeight: 700, marginBottom: 1 }}>
            {tooltip.date}
          </div>
          <div style={{ color: 'var(--ink-4)', marginBottom: 4, fontSize: 9.5 }}>
            {tooltip.time}
          </div>
          {tooltip.straddle != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: C_STRADDLE, flexShrink: 0 }}/>
              <span style={{ color: 'var(--ink-3)' }}>Straddle</span>
              <span style={{ color: 'var(--ink-1)', fontFamily: 'var(--font-data, monospace)', fontWeight: 600 }}>
                {tooltip.straddle.toFixed(2)}
              </span>
            </div>
          )}
          {tooltip.strangle != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: C_STRANGLE, flexShrink: 0 }}/>
              <span style={{ color: 'var(--ink-3)' }}>Strangle</span>
              <span style={{ color: 'var(--ink-1)', fontFamily: 'var(--font-data, monospace)', fontWeight: 600 }}>
                {tooltip.strangle.toFixed(2)}
              </span>
            </div>
          )}
          {tooltip.setNum !== maxSetNum && (
            <div style={{ color: 'var(--ink-4)', fontSize: 9, marginTop: 2 }}>
              Set {tooltip.setNum} (previous)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Locked contracts table ────────────────────────────────────────────────────
// Columns: structure | strike | lock | now | Δ
// lock   = ltp_at_lock from instruments endpoint (exact quote at creation time)
// now    = live tick LTP (nowByRole, same source as LivePulse) — "—" when market closed/stale
// Δ      = now_live − lock, signed; positive = premium expanded from lock
function LockedContractsTable({ series, maxSetNum, lockByRole, nowByRole }) {
  const setNums = Object.keys(series).map(Number).sort((a, b) => a - b);
  if (!setNums.length) return null;

  // Role list comes from lockByRole keys (instruments endpoint, all option legs)
  const allRoles = Object.keys(lockByRole);

  const fmtDelta = (delta) => {
    if (delta == null) return '—';
    return (delta >= 0 ? '+' : '') + fmt2(delta);
  };
  const deltaColor = (delta) => {
    if (delta == null) return 'var(--ink-4)';
    // Negative Δ = decay from lock (favorable for short); positive = expansion (unfavorable)
    return delta < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: 'var(--ink-3)',
        textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6,
      }}>
        Locked Contracts
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: '.5px solid var(--hair)' }}>
            {[
              { h: 'Structure', right: false },
              { h: 'Strike',    right: true  },
              { h: 'Lock',      right: true  },
              { h: 'Now',       right: true  },
              { h: 'Δ',         right: true  },
            ].map(({ h, right }) => (
              <th key={h} style={{
                fontSize: 9.5, fontWeight: 700, color: 'var(--ink-3)',
                textTransform: 'uppercase', letterSpacing: '.06em',
                padding: '4px 8px', textAlign: right ? 'right' : 'left',
                background: 'var(--surface-2)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {setNums.flatMap(setNum => {
            const isActive = setNum === maxSetNum;
            const opacity  = isActive ? 1 : 0.52;

            const setRoles = allRoles.filter(r => parseRole(r)?.setNum === setNum);
            const byStructure = { straddle: [], strangle: [] };
            for (const role of setRoles) {
              const p = parseRole(role);
              if (p) byStructure[p.structure].push({ role, p });
            }

            const tds = (content, right = false, style = {}) => (
              <td style={{
                padding: '3px 8px', textAlign: right ? 'right' : 'left',
                fontFamily: right ? 'var(--font-data, monospace)' : undefined,
                color: 'var(--ink-2)', ...style,
              }}>{content}</td>
            );

            return ['straddle', 'strangle'].flatMap(structure => {
              const legs = byStructure[structure];
              if (!legs.length) return [];
              const ce = legs.find(l => l.p.isCall);
              const pe = legs.find(l => l.p.isPut);

              const ceLock = ce ? (lockByRole[ce.role] ?? null) : null;
              const peLock = pe ? (lockByRole[pe.role] ?? null) : null;
              const ceNow  = ce ? (nowByRole[ce.role]  ?? null) : null;
              const peNow  = pe ? (nowByRole[pe.role]  ?? null) : null;

              const totalLock = ceLock != null && peLock != null ? ceLock + peLock : null;
              const totalNow  = ceNow  != null && peNow  != null ? ceNow  + peNow  : null;
              const totalDelta = totalLock != null && totalNow != null ? totalNow - totalLock : null;
              const ceDelta   = ceLock != null && ceNow  != null ? ceNow  - ceLock  : null;
              const peDelta   = peLock != null && peNow  != null ? peNow  - peLock  : null;

              const color = structure === 'straddle'
                ? (isActive ? C_STRADDLE : C_STRADDLE_FADED)
                : (isActive ? C_STRANGLE : C_STRANGLE_FADED);

              const tableRows = [
                // Structure total row
                <tr key={`${setNum}-${structure}`} style={{ opacity, borderTop: '.5px solid var(--hair)' }}>
                  {tds(
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 9, height: 3, borderRadius: 1, background: color, display: 'inline-block' }}/>
                      <span style={{ fontWeight: 700 }}>
                        Set {setNum} {structure.charAt(0).toUpperCase() + structure.slice(1)}
                      </span>
                    </span>
                  )}
                  {tds('total', true, { color: 'var(--ink-4)', fontSize: 10 })}
                  {tds(<span style={{ fontWeight: 700 }}>{fmt2(totalLock)}</span>, true, { color: 'var(--ink-3)' })}
                  {tds(<span style={{ fontWeight: 700 }}>{fmt2(totalNow)}</span>,  true)}
                  {tds(
                    <span style={{ fontWeight: 700, color: deltaColor(totalDelta) }}>
                      {fmtDelta(totalDelta)}
                    </span>, true
                  )}
                </tr>,
                // CE leg
                ce && (
                  <tr key={`${setNum}-${structure}-ce`} style={{ opacity }}>
                    {tds(
                      <span style={{ paddingLeft: 18, color: 'var(--ink-3)', fontSize: 10.5 }}>
                        └ {structure === 'strangle' ? 'CE wing' : 'CE'}
                      </span>
                    )}
                    {tds(ce.p.contract, true, { color: 'var(--ink-4)', fontSize: 10 })}
                    {tds(fmt2(ceLock), true, { color: 'var(--ink-3)' })}
                    {tds(fmt2(ceNow),  true, { color: 'var(--ink-3)' })}
                    {tds(
                      <span style={{ color: deltaColor(ceDelta) }}>{fmtDelta(ceDelta)}</span>,
                      true
                    )}
                  </tr>
                ),
                // PE leg
                pe && (
                  <tr key={`${setNum}-${structure}-pe`} style={{ opacity }}>
                    {tds(
                      <span style={{ paddingLeft: 18, color: 'var(--ink-3)', fontSize: 10.5 }}>
                        └ {structure === 'strangle' ? 'PE wing' : 'PE'}
                      </span>
                    )}
                    {tds(pe.p.contract, true, { color: 'var(--ink-4)', fontSize: 10 })}
                    {tds(fmt2(peLock), true, { color: 'var(--ink-3)' })}
                    {tds(fmt2(peNow),  true, { color: 'var(--ink-3)' })}
                    {tds(
                      <span style={{ color: deltaColor(peDelta) }}>{fmtDelta(peDelta)}</span>,
                      true
                    )}
                  </tr>
                ),
              ];
              return tableRows.filter(Boolean);
            });
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── NgDecayInner ──────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// SLOT CONTENTS — plugs into ObservationCard via children prop.
// The container renders {children} and has zero knowledge of straddles, sets,
// or greeks. This component has 100% of the NG-specific rendering logic.
// Props: observation, samples (full response from getObservationSamples)
// ═══════════════════════════════════════════════════════════════════════════════
function NgDecayInner({ samples, instruments, live }) {
  const rows = useMemo(() => (samples?.rows || []), [samples]);

  const { series, maxSetNum } = useMemo(
    () => buildPremiumSeries(rows),
    [rows]
  );

  // Lock prices keyed by role_label, from the instruments endpoint
  const lockByRole = useMemo(() => {
    const m = {};
    for (const inst of instruments?.instruments || []) {
      if (inst.role_label && inst.ltp_at_lock != null) {
        m[inst.role_label] = inst.ltp_at_lock;
      }
    }
    return m;
  }, [instruments]);

  // Active set's straddle lock = sum of CE + PE ltp_at_lock (both legs required)
  const straddleLock = useMemo(() => {
    let total = 0, count = 0;
    for (const [role, lock] of Object.entries(lockByRole)) {
      const p = parseRole(role);
      if (p && p.setNum === maxSetNum && p.structure === 'straddle') {
        total += lock; count++;
      }
    }
    return count === 2 ? total : null;
  }, [lockByRole, maxSetNum]);

  // Live LTP per role_label — only populated when market is actually live.
  // When market closed / socket down / stale: empty dict → table + readout show "—".
  const nowByRole = useMemo(() => {
    if (!live?.ng_market_live) return {};
    const m = {};
    for (const inst of live.instruments || []) {
      if (inst.role_label && inst.ltp != null) m[inst.role_label] = inst.ltp;
    }
    return m;
  }, [live]);

  // Readout strip: live straddle/strangle from tick store (same source as LivePulse)
  // null when market closed/stale — readout shows "—" honestly.
  const liveStraddle = live?.ng_market_live ? (live.live_straddle ?? null) : null;
  const liveStrangle = live?.ng_market_live ? (live.live_strangle ?? null) : null;

  // Signed change since lock (live basis)
  const sincelock = straddleLock != null && liveStraddle != null
    ? liveStraddle - straddleLock
    : null;

  const setNums = Object.keys(series).map(Number).sort((a, b) => a - b);

  if (!rows.length) {
    return (
      <div style={{
        padding: '24px 16px', textAlign: 'center',
        color: 'var(--ink-3)', fontSize: 11,
      }}>
        Observation active — awaiting first sample
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 14px 16px' }}>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 7 }}>
        {setNums.map(setNum => {
          const isActive = setNum === maxSetNum;
          return (
            <React.Fragment key={setNum}>
              {['straddle', 'strangle'].map(s => {
                const col = s === 'straddle'
                  ? (isActive ? C_STRADDLE : C_STRADDLE_FADED)
                  : (isActive ? C_STRANGLE : C_STRANGLE_FADED);
                return (
                  <span key={s} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    fontSize: 10, fontWeight: isActive ? 700 : 400,
                    color: isActive ? 'var(--ink-2)' : 'var(--ink-4)',
                  }}>
                    <span style={{
                      display: 'inline-block', width: 18, height: 2,
                      background: col, borderRadius: 1,
                    }}/>
                    Set {setNum} {s}{isActive ? '' : ' (old)'}
                  </span>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Decay chart ── */}
      <div style={{
        background: 'var(--surface-2)', borderRadius: 'var(--r-3)',
        padding: '6px 4px 2px',
      }}>
        <DecayChart series={series} maxSetNum={maxSetNum}/>
      </div>
      <div style={{ fontSize: 9, color: 'var(--ink-4)', marginTop: 4 }}>
        Gap in line = socket_down / reconnect (null sample — not bridged)
      </div>

      {/* ── Locked contracts ── */}
      <LockedContractsTable series={series} maxSetNum={maxSetNum} lockByRole={lockByRole} nowByRole={nowByRole}/>

      {/* ── Current readout strip ── */}
      <div style={{
        marginTop: 14,
        display: 'flex',
        background: 'var(--surface-2)', borderRadius: 'var(--r-3)',
        border: '.5px solid var(--hair)', overflow: 'hidden',
      }}>
        {[
          {
            label: 'Straddle now',
            value: fmt2(liveStraddle),
            color: liveStraddle != null ? C_STRADDLE : 'var(--ink-4)',
          },
          {
            label: 'Strangle now',
            value: fmt2(liveStrangle),
            color: liveStrangle != null ? C_STRANGLE : 'var(--ink-4)',
          },
          {
            label: 'Since lock',
            value: sincelock != null
              ? (sincelock >= 0 ? '+' : '') + fmt2(sincelock)
              : '—',
            color: sincelock == null ? 'var(--ink-4)'
                 : sincelock < 0    ? 'var(--up-field-ink)'
                 :                     'var(--down-field-ink)',
            note: straddleLock != null ? `lock ${fmt2(straddleLock)}` : null,
          },
        ].map(({ label, value, color, note }, i, arr) => (
          <div key={i} style={{
            flex: 1, padding: '8px 10px',
            borderRight: i < arr.length - 1 ? '.5px solid var(--hair)' : 'none',
          }}>
            <div style={{
              fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.07em',
              color: 'var(--ink-3)', marginBottom: 4,
            }}>
              {label}
            </div>
            <div style={{
              fontSize: 17, fontWeight: 700,
              fontFamily: 'var(--font-data, monospace)',
              color,
            }}>
              {value}
            </div>
            {note && (
              <div style={{ fontSize: 9, color: 'var(--ink-4)', marginTop: 2 }}>
                {note}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Live pulse helpers ────────────────────────────────────────────────────────

function fmtAgeSeconds(sec) {
  if (sec == null) return null;
  if (sec < 60)   return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`;
}

// Returns { text, dimmed, color } for the re-strike status line
function restrikeLabel(rs) {
  if (!rs) return null;
  if (rs.state === 'floor_not_met') {
    return {
      text:   `re-strike: floor clears in ${rs.days_remaining}d`,
      dimmed: true,
      color:  'var(--ink-3)',
    };
  }
  if (rs.state === 'no_futures_tick') {
    return {
      text:   're-strike: no futures tick',
      dimmed: true,
      color:  'var(--ink-4)',
    };
  }
  const armed = rs.state === 'armed';
  return {
    text:  armed
      ? `re-strike: ARMED · ${fmt2(rs.drift_points)} / ${fmt2(rs.drift_threshold)} pts drift`
      : `re-strike: watching · ${fmt2(rs.drift_points)} / ${fmt2(rs.drift_threshold)} pts drift`,
    dimmed: !armed,
    color:  armed ? 'var(--down-field-ink)' : 'var(--ink-3)',
  };
}

// ── LivePulse ─────────────────────────────────────────────────────────────────
// Shows always-current futures price, distance from ATM, and live premiums.
// Honesty rule: only shows numeric values when ng_market_live=true AND tick is fresh.
// Collapsed variant (compact=true): one-liner for the triage row.
// Expanded variant (compact=false): multi-value row for the header band / health section.
function LivePulse({ live, compact = false }) {
  if (!live) return null;

  const {
    ng_market_live,
    socket_connected,
    futures_ltp,
    futures_age_seconds,
    distance_from_atm,
    live_straddle,
    live_strangle,
    active_set,
    restrike_status,
    last_disconnect_reason,
  } = live;

  // Determine freshness state
  const isLive = ng_market_live === true;
  const isDown = !socket_connected;

  // Age label shown next to values when live
  const ageStr = fmtAgeSeconds(futures_age_seconds);

  // Distance sign and color
  const distAbs = distance_from_atm != null ? Math.abs(distance_from_atm) : null;
  const distColor = distAbs != null && distAbs >= 20
    ? 'var(--down-field-ink)'   // approaching threshold → amber/red
    : 'var(--ink-2)';

  if (compact) {
    // ── Collapsed triage row pulse ──
    if (isDown) {
      return (
        <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>
          {socket_connected === false ? 'socket down' : 'not connected'}
        </span>
      );
    }
    if (!isLive) {
      return (
        <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>market closed</span>
      );
    }
    // Live — show futures + distance + premiums
    const struck = active_set?.struck_atm;
    const distLabel = distAbs != null && struck != null
      ? ` · ${fmt2(distAbs)} from ATM ${fmt2(struck)}`
      : '';
    const premLabel = live_straddle != null
      ? ` · St ${fmt2(live_straddle)}${live_strangle != null ? ` · Sg ${fmt2(live_strangle)}` : ''}`
      : '';
    return (
      <span style={{ fontSize: 10, color: 'var(--ink-2)', fontFamily: 'var(--font-data, monospace)' }}>
        Fut {fmt2(futures_ltp)}{distLabel}{premLabel}
        {ageStr && (
          <span style={{ color: 'var(--ink-4)', fontFamily: 'inherit', marginLeft: 4 }}>
            {ageStr}
          </span>
        )}
      </span>
    );
  }

  // ── Expanded card pulse block ──
  const rs = restrikeLabel(restrike_status);

  if (isDown) {
    return (
      <div style={{ padding: '8px 12px', borderBottom: '.5px solid var(--hair)' }}>
        <div style={{ fontSize: 10, color: 'var(--ink-4)' }}>
          <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Live Pulse
          </span>
          {' '}·{' '}
          socket down{last_disconnect_reason ? ` — ${last_disconnect_reason}` : ''}
        </div>
      </div>
    );
  }

  if (!isLive) {
    return (
      <div style={{ padding: '8px 12px', borderBottom: '.5px solid var(--hair)' }}>
        <div style={{ fontSize: 10, color: 'var(--ink-4)' }}>
          <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Live Pulse
          </span>
          {' '}· market closed
          {futures_ltp != null && ageStr && (
            <span style={{ marginLeft: 6 }}>
              (last fut {fmt2(futures_ltp)}, {ageStr} — stale)
            </span>
          )}
        </div>
        {rs && (
          <div style={{ marginTop: 3, fontSize: 10, color: rs.color }}>
            {rs.text}
          </div>
        )}
      </div>
    );
  }

  // Live
  const struck = active_set?.struck_atm;
  return (
    <div style={{
      padding: '8px 12px',
      borderBottom: '.5px solid var(--hair)',
      background: 'rgba(44, 162, 83, 0.035)',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{
          fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '.06em', color: '#1d7a3c',
        }}>
          Live Pulse
        </span>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2CA253', display: 'inline-block' }}/>
        {ageStr && (
          <span style={{ fontSize: 9, color: 'var(--ink-4)', marginLeft: 2 }}>
            {ageStr}
          </span>
        )}
      </div>

      {/* Value cells */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
        {/* Futures + distance */}
        <div>
          <span style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Futures
          </span>
          {' '}
          <span style={{
            fontSize: 13, fontWeight: 700,
            fontFamily: 'var(--font-data, monospace)', color: 'var(--ink)',
          }}>
            {fmt2(futures_ltp)}
          </span>
          {distAbs != null && struck != null && (
            <span style={{
              fontSize: 10, color: distColor, marginLeft: 6,
              fontFamily: 'var(--font-data, monospace)',
            }}>
              {fmt2(distAbs)} from {fmt2(struck)}
            </span>
          )}
        </div>

        {/* Separator */}
        <span style={{ color: 'var(--hair-strong)', fontSize: 12, alignSelf: 'center' }}>|</span>

        {/* Live straddle */}
        {live_straddle != null && (
          <div>
            <span style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Straddle
            </span>
            {' '}
            <span style={{
              fontSize: 13, fontWeight: 700,
              fontFamily: 'var(--font-data, monospace)', color: C_STRADDLE,
            }}>
              {fmt2(live_straddle)}
            </span>
          </div>
        )}

        {/* Live strangle */}
        {live_strangle != null && (
          <div>
            <span style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Strangle
            </span>
            {' '}
            <span style={{
              fontSize: 13, fontWeight: 700,
              fontFamily: 'var(--font-data, monospace)', color: C_STRANGLE,
            }}>
              {fmt2(live_strangle)}
            </span>
          </div>
        )}
      </div>

      {/* Re-strike line */}
      {rs && (
        <div style={{ marginTop: 4, fontSize: 10, color: rs.color }}>
          {rs.text}
        </div>
      )}
    </div>
  );
}

// ── HealthLines ───────────────────────────────────────────────────────────────
// Health status block for the expanded card — socket state + sample schedule.
// Separate from LivePulse so ObservationCard can place them independently.
function HealthLines({ live, nextSample, lastReadingAt }) {
  if (!live) return null;
  const { socket_connected, last_disconnect_reason } = live;

  const socketLabel = socket_connected
    ? <span style={{ color: '#1d7a3c' }}>connected</span>
    : <span style={{ color: 'var(--down-field-ink)' }}>
        down{last_disconnect_reason ? ` — ${last_disconnect_reason}` : ''}
      </span>;

  const lastRealStr = lastReadingAt
    ? `${fmtTimeIST(lastReadingAt)} (${fmtAge(lastReadingAt)})`
    : 'none yet';

  return (
    <div style={{
      padding: '6px 12px 6px',
      borderBottom: '.5px solid var(--hair)',
      display: 'flex', flexWrap: 'wrap', gap: '3px 16px',
      fontSize: 10, color: 'var(--ink-3)',
    }}>
      <span>socket: {socketLabel}</span>
      <span style={{ color: 'var(--hair-strong)' }}>|</span>
      <span>last real: {lastRealStr}</span>
      {nextSample && (
        <>
          <span style={{ color: 'var(--hair-strong)' }}>|</span>
          <span style={{ color: nextSample.dim ? 'var(--ink-4)' : 'var(--ink-3)' }}>
            {nextSample.text}
          </span>
        </>
      )}
    </div>
  );
}

// ── Kind metadata — ticker symbol + measures label per observation kind ────────
const KIND_META = {
  ng_straddle_strangle: { ticker: 'NATURALGAS', sector: 'Commodity', measures: 'straddle / strangle' },
  nifty_decay:          { ticker: 'NIFTY',      sector: 'Index',     measures: 'premium decay'       },
  nifty_delta_decay:    { ticker: 'NIFTY',      sector: 'Index',     measures: 'delta decay'         },
  nifty_straddle:       { ticker: 'NIFTY',      sector: 'Index',     measures: 'ATM straddle'        },
  sensex_straddle:      { ticker: 'SENSEX',     sector: 'Index',     measures: 'ATM straddle'        },
};
const DEFAULT_KIND_META = { ticker: null, sector: '', measures: 'observation' };

// ── Header slot 3: value-now ──────────────────────────────────────────────────
// Live straddle/strangle + muted futures distance. Market-aware honesty.
function HeaderValueNow({ live }) {
  if (!live) return <span style={{ color: 'var(--ink-4)', fontSize: 11 }}>—</span>;

  const { ng_market_live, live_straddle, live_strangle, futures_ltp, futures_age_seconds, distance_from_atm, active_set } = live;

  if (!live.socket_connected) {
    return <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>not connected</span>;
  }

  if (!ng_market_live) {
    // Honest closed state — show last known values clearly marked stale
    const staleAge = fmtAgeSeconds(futures_age_seconds);
    return (
      <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>
        market closed
        {live_straddle != null && (
          <span style={{ marginLeft: 5 }}>
            · last St {fmt2(live_straddle)}{staleAge ? ` · ${staleAge}` : ''}
          </span>
        )}
      </span>
    );
  }

  // Live
  const struck = active_set?.struck_atm;
  const distAbs = distance_from_atm != null ? Math.abs(distance_from_atm) : null;

  return (
    <span style={{ fontSize: 11, color: 'var(--ink-2)', fontFamily: 'var(--font-data, monospace)' }}>
      {live_straddle != null && (
        <span>St <strong>{fmt2(live_straddle)}</strong></span>
      )}
      {live_strangle != null && (
        <span style={{ marginLeft: 6 }}>· Sg <strong>{fmt2(live_strangle)}</strong></span>
      )}
      {futures_ltp != null && (
        <span style={{ marginLeft: 8, color: 'var(--ink-3)', fontSize: 10.5 }}>
          · Fut {fmt2(futures_ltp)}
          {distAbs != null && struck != null && (
            <span style={{ color: distAbs >= 20 ? 'var(--down-field-ink)' : 'var(--ink-3)' }}>
              {' '}· {fmt2(distAbs)} from {fmt2(struck)}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

// ── Header slot 4: status / timestamps ───────────────────────────────────────
function HeaderStatus({ live, nextSample, lastReadingAt }) {
  const parts = [];

  if (live?.ng_market_live && live.futures_age_seconds != null) {
    const s = Math.round(live.futures_age_seconds);
    parts.push(`live ${s}s`);
  }

  if (nextSample) {
    parts.push(nextSample.text);
  } else if (lastReadingAt) {
    parts.push(fmtAge(lastReadingAt));
  }

  if (!parts.length) return <span style={{ color: 'var(--ink-4)', fontSize: 10 }}>—</span>;

  return (
    <span style={{
      fontSize: 10, color: 'var(--ink-4)', textAlign: 'right',
      lineHeight: 1.5, whiteSpace: 'nowrap',
    }}>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span style={{ margin: '0 4px', opacity: 0.5 }}>·</span>}
          {p}
        </span>
      ))}
    </span>
  );
}

// ── ObservationCard — container, inner-agnostic ───────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// SLOT BOUNDARY: `children` receives the inner surface.
//
// Header is a 6-column CSS grid — identical in collapsed AND expanded state.
// Expanding only reveals the body below; the header never changes.
//
// Grid: [dot 14px] [ticker 160px] [measures 130px] [value-now 1fr] [status 170px] [chevron 16px]
//
// Props:
//   observation   — { id, name, kind, status, interval_seconds, created_at }
//   deskStatus    — 6-state string from /api/desk/status
//   lastReadingAt — ISO string of latest REAL sample timestamp (gap rows excluded)
//   nextSample    — { text, dim } from computeNextSample(); null if no data yet
//   live          — response from getObservationLive(); drives header slots + body
//   children      — inner surface (chart, locked-contracts table, readout)
// ═══════════════════════════════════════════════════════════════════════════════
function ObservationCard({ observation, deskStatus, lastReadingAt, nextSample, live, headerValue, children }) {
  const [open, setOpen] = useState(false);
  const obs  = observation || {};
  const state = deskStatus || 'NOT_CONNECTED';
  const km   = KIND_META[obs.kind] || DEFAULT_KIND_META;

  return (
    <div className="card" style={{ marginBottom: 10 }}>

      {/* ── Fixed-slot grid header — same in collapsed AND expanded ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
        style={{
          display: 'grid',
          gridTemplateColumns: '14px 160px 130px 1fr 170px 16px',
          alignItems: 'center',
          gap: '0 10px',
          padding: '10px 12px',
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: open ? '.5px solid var(--hair)' : 'none',
        }}
      >
        {/* Col 1 — status dot */}
        <StatusDot state={state}/>

        {/* Col 2 — ticker (stopPropagation so click opens SidePanel, not card toggle) */}
        <div onClick={e => e.stopPropagation()} style={{ minWidth: 0 }}>
          {km.ticker
            ? <Ticker name={km.ticker} sector={km.sector}/>
            : <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{obs.name || `#${obs.id}`}</span>
          }
        </div>

        {/* Col 3 — measures */}
        <span style={{
          fontSize: 11, color: 'var(--ink-3)', fontWeight: 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {km.measures}
        </span>

        {/* Col 4 — value now (live straddle/strangle + futures) */}
        {headerValue !== undefined ? headerValue : <HeaderValueNow live={live}/>}

        {/* Col 5 — status / freshness / next-sample */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <HeaderStatus live={live} nextSample={nextSample} lastReadingAt={lastReadingAt}/>
        </div>

        {/* Col 6 — expand chevron */}
        <span style={{
          fontSize: 11, color: 'var(--ink-3)',
          transition: 'transform .15s',
          transform: open ? 'rotate(180deg)' : 'none',
          justifySelf: 'end',
        }}>▾</span>
      </div>

      {/* ── Expanded body — header above never changes ── */}
      {open && (
        <>
          {/* ── Live pulse (expanded) — always-current tick values ── */}
          <LivePulse live={live} compact={false}/>

          {/* ── Health lines — socket state + next-sample schedule ── */}
          <HealthLines live={live} nextSample={nextSample} lastReadingAt={lastReadingAt}/>

          {/* ── SLOT — the container renders whatever was passed as children ── */}
          {children}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NIFTY observation cards — nifty_decay and nifty_delta_decay
// ═══════════════════════════════════════════════════════════════════════════════

// Parse a NIFTY role_label like "near-week 24850CE" → { expiryLabel, strike, optType }
function parseNiftyRole(label) {
  if (!label || label === 'spot') return null;
  const m = label.match(/^(.+?)\s+(\d+)(CE|PE)$/);
  if (!m) return null;
  return { expiryLabel: m[1], strike: Number(m[2]), optType: m[3] };
}

// Build a per-leg [{ t, v }] series from an obs's sample rows.
// v=null when gap_reason is set — never bridged.
function buildLegSeries(rows, roleLabel) {
  return rows
    .filter(r => r.role_label === roleLabel)
    .sort((a, b) => (a.captured_at < b.captured_at ? -1 : 1))
    .map(r => ({ t: r.captured_at, v: r.gap_reason ? null : r.ltp }));
}

// Return the last non-gap LTP for a leg from sample rows (newest-first from API).
function legLastReal(rows, roleLabel) {
  return rows.find(r => r.role_label === roleLabel && !r.gap_reason && r.ltp != null) ?? null;
}

// IST date label like "9 Jul" from any ISO string (UTC or +05:30).
function istDayLabel(isoStr) {
  const ms = utcDate(isoStr)?.getTime();
  if (!ms) return '?';
  return new Date(ms).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short',
  });
}

// IST time string "HH:MM" from any ISO string.
function istTimeHHMM(isoStr) {
  const ms = utcDate(isoStr)?.getTime();
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

// 10-color palette for per-leg chart lines.
const NIFTY_CHART_COLORS = [
  '#6c63e8', '#e07b2a', '#2ca253', '#c73a3a', '#2a78d6',
  '#e879a0', '#0891b2', '#a16207', '#8b5cf6', '#166534',
];
const NIFTY_MAX_SELECTED = 10;

// ── NiftyPerLegChart ─────────────────────────────────────────────────────────
// X = IST time-of-day 09:00–15:35 (all selected days stack on same axis).
// One line per selected leg. Gaps are breaks. No line before first sample.
function NiftyPerLegChart({ allLegSeries, selectedLegs, colorMap, legLabels }) {
  const SESSION_START = 9 * 3600;
  const SESSION_END   = 15 * 3600 + 35 * 60;

  const PAD = { t: 10, r: 14, b: 28, l: 42 };
  const VW = 600, VH = 195;
  const innerW = VW - PAD.l - PAD.r;
  const innerH = VH - PAD.t - PAD.b;

  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  // Flatten all non-null points across selected legs for scale
  const allNonNull = useMemo(() => {
    const pts = [];
    for (const key of selectedLegs) {
      const series = allLegSeries.get(key) || [];
      for (const p of series) { if (p.v != null) pts.push({ ...p, key }); }
    }
    return pts;
  }, [allLegSeries, selectedLegs]);

  if (!selectedLegs.size) {
    return (
      <div style={{
        height: VH, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-4)', fontSize: 11,
      }}>
        Tick a checkbox to plot a leg
      </div>
    );
  }
  if (!allNonNull.length) {
    return (
      <div style={{
        height: VH, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-4)', fontSize: 11,
      }}>
        No data for selected legs
      </div>
    );
  }

  const xOf = (isoStr) => {
    const ms   = utcDate(isoStr).getTime();
    const sec  = istSecOfDay(ms);
    const frac = Math.max(0, Math.min(1, (sec - SESSION_START) / (SESSION_END - SESSION_START)));
    return PAD.l + frac * innerW;
  };

  const vMax = Math.max(...allNonNull.map(p => p.v)) * 1.18 || 1;
  const yOf  = (v) => PAD.t + (1 - v / vMax) * innerH;

  const gridVals = [0, 0.25, 0.5, 0.75, 1].map(f => f * vMax);

  const xLabels = [];
  for (let h = 9; h <= 15; h++) {
    const sec  = h * 3600;
    const frac = (sec - SESSION_START) / (SESSION_END - SESSION_START);
    if (frac >= 0 && frac <= 1) {
      xLabels.push({ x: PAD.l + frac * innerW, label: `${String(h).padStart(2,'0')}:00` });
    }
  }

  const handleMouseMove = (e) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const pt = svgEl.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svgEl.getScreenCTM().inverse());
    const svgX  = svgPt.x;
    if (svgX < PAD.l || svgX > VW - PAD.r) { setTooltip(null); return; }

    let best = null, bestDist = Infinity;
    for (const p of allNonNull) {
      const d = Math.abs(xOf(p.t) - svgX);
      if (d < bestDist) { bestDist = d; best = p; }
    }
    if (!best || bestDist > 20) { setTooltip(null); return; }

    // Snap to this timestamp — collect all selected legs' values at this time-of-day
    // (best-effort: find point with same seconds-of-day within ±90s per leg)
    const snapSec = istSecOfDay(utcDate(best.t).getTime());
    const snapX   = xOf(best.t);
    const tipOnLeft = snapX > VW * 0.62;

    const values = [];
    for (const key of selectedLegs) {
      const series = allLegSeries.get(key) || [];
      const closest = series
        .filter(p => p.v != null)
        .sort((a, b) => {
          const da = Math.abs(istSecOfDay(utcDate(a.t).getTime()) - snapSec);
          const db = Math.abs(istSecOfDay(utcDate(b.t).getTime()) - snapSec);
          return da - db;
        })[0];
      if (closest && Math.abs(istSecOfDay(utcDate(closest.t).getTime()) - snapSec) <= 90) {
        values.push({ key, v: closest.v, color: colorMap[key] });
      }
    }

    const ms      = utcDate(best.t).getTime();
    const sec     = istSecOfDay(ms);
    const hh      = String(Math.floor(sec / 3600)).padStart(2, '0');
    const mm      = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
    const ss      = String(sec % 60).padStart(2, '0');
    setTooltip({ svgX: snapX, tipOnLeft, time: `${hh}:${mm}:${ss} IST`, values });
  };

  return (
    <div style={{ position: 'relative', userSelect: 'none' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width: '100%', height: VH, display: 'block', cursor: 'crosshair' }}
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {gridVals.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={yOf(v)} x2={VW - PAD.r} y2={yOf(v)}
              stroke="var(--hair)" strokeWidth={0.75}/>
            <text x={PAD.l - 6} y={yOf(v) + 3.5}
              textAnchor="end" fontSize={9} fill="var(--ink-3)">{v.toFixed(0)}</text>
          </g>
        ))}
        <line x1={PAD.l} y1={VH - PAD.b} x2={VW - PAD.r} y2={VH - PAD.b}
          stroke="var(--hair-strong)" strokeWidth={0.75}/>
        {xLabels.map(({ x, label }) => (
          <text key={label} x={x} y={VH - PAD.b + 13}
            textAnchor="middle" fontSize={9} fill="var(--ink-3)">{label}</text>
        ))}

        {[...selectedLegs].map(key => {
          const series = allLegSeries.get(key) || [];
          const color  = colorMap[key] || '#888';
          const segs   = toSegments(series);
          return (
            <g key={key}>
              {segs.map((seg, i) => {
                if (seg.length === 1) {
                  return <circle key={i} cx={xOf(seg[0].t)} cy={yOf(seg[0].v)} r={2.5} fill={color}/>;
                }
                const pts = seg.map(p => `${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' ');
                return (
                  <polyline key={i} points={pts}
                    fill="none" stroke={color} strokeWidth={1.75}
                    strokeLinejoin="round" strokeLinecap="round"/>
                );
              })}
            </g>
          );
        })}

        {tooltip && (
          <line x1={tooltip.svgX} y1={PAD.t} x2={tooltip.svgX} y2={VH - PAD.b}
            stroke="var(--ink-3)" strokeWidth={0.75} strokeDasharray="3 2" pointerEvents="none"/>
        )}
        {tooltip && tooltip.values.map(({ key, v, color }) => (
          v != null && (
            <circle key={key} cx={tooltip.svgX} cy={yOf(v)} r={3.5}
              fill={color} stroke="var(--surface-1)" strokeWidth={1.5} pointerEvents="none"/>
          )
        ))}
      </svg>

      {tooltip && (
        <div style={{
          position: 'absolute', top: 8,
          left:  tooltip.tipOnLeft ? undefined : `calc(${(tooltip.svgX / VW) * 100}% + 8px)`,
          right: tooltip.tipOnLeft ? `calc(${100 - (tooltip.svgX / VW) * 100}% + 8px)` : undefined,
          pointerEvents: 'none',
          background: 'var(--surface-1)', border: '.5px solid var(--hair-strong)',
          borderRadius: 'var(--r-2)', padding: '5px 8px',
          fontSize: 10, lineHeight: 1.7, whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,.12)', zIndex: 10,
        }}>
          <div style={{ color: 'var(--ink-4)', fontSize: 9.5, marginBottom: 3 }}>{tooltip.time}</div>
          {tooltip.values.map(({ key, v, color }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }}/>
              <span style={{ color: 'var(--ink-3)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {legLabels[key] || key}
              </span>
              <span style={{ color: 'var(--ink-1)', fontFamily: 'var(--font-data, monospace)', fontWeight: 600, marginLeft: 'auto', paddingLeft: 8 }}>
                {v != null ? v.toFixed(2) : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── NiftyDayTable ─────────────────────────────────────────────────────────────
// Compact collapsible table: one row per obs (day), expandable to show legs.
// Drives the chart via checkbox selection.
function NiftyDayTable({ obsDataList, selectedLegs, onToggleLeg, colorMap, liveDataMap, marketLiveId }) {
  const [openDays, setOpenDays] = useState(new Set());

  const toggleDay = (id) => setOpenDays(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const selectedCount = selectedLegs.size;

  if (!obsDataList.length) {
    return (
      <div style={{ padding: '16px 0', color: 'var(--ink-4)', fontSize: 11 }}>
        No observations recorded yet.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 2 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: 'var(--ink-3)',
          textTransform: 'uppercase', letterSpacing: '.07em',
        }}>
          Days
        </div>
        <div style={{ fontSize: 10, color: selectedCount >= NIFTY_MAX_SELECTED ? 'var(--down-field-ink)' : 'var(--ink-4)' }}>
          {selectedCount} / {NIFTY_MAX_SELECTED} selected
        </div>
      </div>

      {obsDataList.map(({ obs, samples, instruments }) => {
        const rows        = samples?.rows || [];
        const instList    = instruments?.instruments || [];
        const optInsts    = instList.filter(i => i.role_label !== 'spot');
        const isOpen      = openDays.has(obs.id);
        const isActive    = obs.status === 'active';
        const liveData    = liveDataMap[obs.id] ?? null;
        const mktLive     = isActive && marketLiveId === obs.id && liveData?.market_live === true;

        // Day header values
        const dayLabel    = istDayLabel(obs.created_at);
        const lockTime    = istTimeHHMM(obs.created_at);
        const spotRows    = rows.filter(r => r.role_label === 'spot' && !r.gap_reason && r.ltp != null)
                               .sort((a, b) => a.captured_at < b.captured_at ? -1 : 1);
        const spotOpen    = spotRows[0]?.ltp ?? null;
        const spotClose   = mktLive ? (liveData?.spot?.ltp ?? spotRows[spotRows.length - 1]?.ltp ?? null)
                                    : (spotRows[spotRows.length - 1]?.ltp ?? null);
        const spotMove    = spotOpen != null && spotClose != null ? spotClose - spotOpen : null;

        // Last real sample time (for greeks staleness label)
        const lastRealRow = rows.find(r => r.role_label !== 'spot' && !r.gap_reason && r.ltp != null);
        const greeksLabel = mktLive
          ? 'Greeks (live)'
          : lastRealRow
            ? `Greeks · ${istTimeHHMM(lastRealRow.captured_at)}`
            : 'Greeks';

        const fmtSpot = (v) => v != null ? Math.round(v).toLocaleString('en-IN') : '—';
        const signedMove = spotMove != null
          ? `${spotMove >= 0 ? '+' : ''}${Math.round(spotMove).toLocaleString('en-IN')}`
          : null;
        const moveColor = spotMove == null ? 'var(--ink-4)' : spotMove >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';

        return (
          <div key={obs.id} style={{
            border: '.5px solid var(--hair)', borderRadius: 'var(--r-3)',
            marginBottom: 6, overflow: 'hidden',
          }}>
            {/* Day header row */}
            <div
              role="button" tabIndex={0}
              onClick={() => toggleDay(obs.id)}
              onKeyDown={e => e.key === 'Enter' && toggleDay(obs.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', cursor: 'pointer',
                background: isOpen ? 'var(--surface-2)' : 'var(--surface-1)',
                userSelect: 'none',
              }}
            >
              <span style={{
                fontSize: 10, color: 'var(--ink-3)',
                transform: isOpen ? 'rotate(90deg)' : 'none',
                display: 'inline-block', transition: 'transform .12s', flexShrink: 0,
              }}>▸</span>

              <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? 'var(--up-field-ink)' : 'var(--ink-2)', flexShrink: 0 }}>
                {dayLabel}
              </span>

              <span style={{ fontSize: 10, color: 'var(--ink-4)', flexShrink: 0 }}>
                locked {lockTime}
              </span>

              <span style={{ fontSize: 10, color: 'var(--ink-3)', flexShrink: 0 }}>
                spot{' '}
                <span style={{ fontFamily: 'var(--font-data, monospace)' }}>{fmtSpot(spotOpen)}</span>
                {' → '}
                <span style={{ fontFamily: 'var(--font-data, monospace)' }}>{fmtSpot(spotClose)}</span>
                {signedMove != null && (
                  <span style={{ color: moveColor, fontFamily: 'var(--font-data, monospace)', marginLeft: 4 }}>
                    ({signedMove})
                  </span>
                )}
              </span>

              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ink-4)', flexShrink: 0 }}>
                {optInsts.length} legs
              </span>
            </div>

            {/* Expanded leg rows */}
            {isOpen && (
              <div style={{ borderTop: '.5px solid var(--hair)' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-2)' }}>
                        {[
                          { h: '',          right: false, w: 20  },
                          { h: 'Leg',       right: false         },
                          { h: 'Strike',    right: true          },
                          { h: 'Lock',      right: true          },
                          { h: mktLive ? 'Now' : 'Close', right: true },
                          { h: 'Δ',         right: true          },
                          { h: '%',         right: true          },
                          { h: 'IV',        right: true          },
                          { h: 'Delta',     right: true          },
                          { h: 'Theta',     right: true          },
                        ].map(({ h, right, w }) => (
                          <th key={h} style={{
                            fontSize: 9.5, fontWeight: 700, color: h.startsWith('Greeks') ? 'var(--ink-4)' : 'var(--ink-3)',
                            textTransform: 'uppercase', letterSpacing: '.06em',
                            padding: '4px 6px', textAlign: right ? 'right' : 'left',
                            whiteSpace: 'nowrap', width: w,
                          }}>{h}</th>
                        ))}
                      </tr>
                      {/* Greeks staleness label */}
                      {!mktLive && lastRealRow && (
                        <tr style={{ background: 'var(--surface-2)' }}>
                          <td colSpan={7}/>
                          <td colSpan={3} style={{
                            fontSize: 9, color: 'var(--ink-4)', padding: '0 6px 4px',
                            textAlign: 'right', fontStyle: 'italic',
                          }}>
                            {greeksLabel}
                          </td>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {optInsts.map(inst => {
                        const p   = parseNiftyRole(inst.role_label);
                        if (!p) return null;
                        const key = `${obs.id}:${inst.role_label}`;
                        const lk  = inst.ltp_at_lock;

                        // Close price: live if market open, else last real sample
                        const liveleg  = mktLive ? liveData.legs.find(l => l.role_label === inst.role_label) : null;
                        const closeRow = legLastReal(rows, inst.role_label);
                        const closeVal = mktLive && liveleg?.ltp_now != null ? liveleg.ltp_now
                                       : closeRow?.ltp ?? null;

                        const absDelta  = closeVal != null && lk != null ? closeVal - lk : null;
                        const pctDecay  = absDelta != null && lk > 0 ? (absDelta / lk) * 100 : null;

                        // Greeks: live if market open, else last real sample
                        const iv    = (mktLive && liveleg?.iv    != null) ? liveleg.iv    : (closeRow?.iv    ?? null);
                        const delta = (mktLive && liveleg?.delta != null) ? liveleg.delta : (closeRow?.delta ?? null);
                        const theta = (mktLive && liveleg?.theta != null) ? liveleg.theta : (closeRow?.theta ?? null);

                        const dColor = (d) => d == null ? 'var(--ink-4)' : d < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
                        const fmt2n  = (v) => v != null ? v.toFixed(2) : '—';
                        const isChecked = selectedLegs.has(key);
                        const isDisabled = !isChecked && selectedCount >= NIFTY_MAX_SELECTED;

                        const rowColor = isChecked ? colorMap[key] : null;

                        return (
                          <tr
                            key={key}
                            style={{
                              borderTop: '.5px solid var(--hair)',
                              background: isChecked ? `${rowColor}12` : undefined,
                            }}
                          >
                            {/* Checkbox */}
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={() => onToggleLeg(key)}
                                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', accentColor: rowColor || undefined }}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            {/* Leg label with color swatch */}
                            <td style={{ padding: '4px 6px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                {isChecked && (
                                  <span style={{
                                    display: 'inline-block', width: 8, height: 8,
                                    borderRadius: '50%', background: rowColor, flexShrink: 0,
                                  }}/>
                                )}
                                <span style={{ color: 'var(--ink-3)', fontSize: 10.5 }}>
                                  {p.expiryLabel.replace(/-/g, ' ')} {p.optType}
                                </span>
                              </span>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>
                              {p.strike.toLocaleString('en-IN')}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>
                              {fmt2n(lk)}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)' }}>
                              {fmt2n(closeVal)}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: dColor(absDelta) }}>
                              {absDelta != null ? (absDelta >= 0 ? '+' : '') + absDelta.toFixed(2) : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: dColor(pctDecay), fontWeight: 700 }}>
                              {pctDecay != null ? (pctDecay >= 0 ? '+' : '') + pctDecay.toFixed(1) + '%' : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>
                              {iv != null ? iv.toFixed(1) + '%' : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>
                              {delta != null ? delta.toFixed(4) : '—'}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>
                              {theta != null ? theta.toFixed(4) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── NiftyKindCard ─────────────────────────────────────────────────────────────
// ONE card per kind (nifty_decay / nifty_delta_decay).
// Loads ALL observations of that kind, renders a collapsible day-table + per-leg chart.
function NiftyKindCard({ kind, obsList, deskStatus }) {
  // Filter obs of this kind, newest-first
  const kindObs = useMemo(
    () => (obsList || []).filter(o => o.kind === kind),
    [obsList, kind]
  );

  // Per-obs data: { obs, samples, instruments }
  const [obsData, setObsData] = useState([]);

  // Live data keyed by obs_id — only for active obs
  const [liveDataMap, setLiveDataMap] = useState({});

  // Selected legs: Set of "obsId:roleLabel" strings
  const [selectedLegs, setSelectedLegs] = useState(new Set());

  // Color map: legKey → color (assigned in selection order)
  const [colorMap, setColorMap] = useState({});

  // Load all obs data on mount / when kindObs changes
  useEffect(() => {
    if (!kindObs.length) return;
    let cancelled = false;

    const loadAll = async () => {
      const results = await Promise.allSettled(
        kindObs.map(obs =>
          Promise.allSettled([
            getObservationSamples(obs.id, 1000),
            getObservationInstruments(obs.id),
          ]).then(([sRes, iRes]) => ({
            obs,
            samples:     sRes.status === 'fulfilled' ? sRes.value : null,
            instruments: iRes.status === 'fulfilled' ? iRes.value : null,
          }))
        )
      );
      if (!cancelled) {
        const loaded = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);
        setObsData(loaded);

        // Auto-select all legs of the most recent obs (up to NIFTY_MAX_SELECTED)
        setSelectedLegs(prev => {
          if (prev.size > 0) return prev; // don't clobber user's selection on refresh
          const first = loaded[0];
          if (!first) return prev;
          const legs = (first.instruments?.instruments || [])
            .filter(i => i.role_label !== 'spot')
            .slice(0, NIFTY_MAX_SELECTED);
          const keys = legs.map(i => `${first.obs.id}:${i.role_label}`);
          const newSet = new Set(keys);
          const newColorMap = {};
          keys.forEach((k, idx) => { newColorMap[k] = NIFTY_CHART_COLORS[idx]; });
          setColorMap(newColorMap);
          return newSet;
        });
      }
    };

    loadAll();
    const t = setInterval(loadAll, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [kindObs]);

  // Load live data for any active obs
  useEffect(() => {
    const activeObs = kindObs.filter(o => o.status === 'active');
    if (!activeObs.length) return;
    let cancelled = false;

    const loadLive = async () => {
      const results = await Promise.allSettled(activeObs.map(o => getNiftyObsLive(o.id)));
      if (!cancelled) {
        const map = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value) map[activeObs[i].id] = r.value;
        });
        setLiveDataMap(map);
      }
    };

    loadLive();
    const t = setInterval(loadLive, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [kindObs]);

  const toggleLeg = useCallback((key) => {
    setSelectedLegs(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setColorMap(cm => { const n = { ...cm }; delete n[key]; return n; });
      } else {
        if (next.size >= NIFTY_MAX_SELECTED) return prev;
        next.add(key);
        // Assign the first unused color slot
        setColorMap(cm => {
          const usedColors = new Set(Object.values(cm));
          const freeColor  = NIFTY_CHART_COLORS.find(c => !usedColors.has(c)) || NIFTY_CHART_COLORS[0];
          return { ...cm, [key]: freeColor };
        });
      }
      return next;
    });
  }, []);

  // Build allLegSeries (Map) and legLabels (obj) from obsData
  const { allLegSeries, legLabels } = useMemo(() => {
    const seriesMap = new Map();
    const labels    = {};
    for (const { obs, samples } of obsData) {
      const rows    = samples?.rows || [];
      const dayLbl  = istDayLabel(obs.created_at);
      const optRoles = [...new Set(rows.filter(r => r.role_label !== 'spot').map(r => r.role_label))];
      for (const role of optRoles) {
        const key = `${obs.id}:${role}`;
        seriesMap.set(key, buildLegSeries(rows, role));
        const p = parseNiftyRole(role);
        labels[key] = p
          ? `${dayLbl} · ${p.expiryLabel.replace(/-/g, ' ')} ${p.optType} ${p.strike.toLocaleString('en-IN')}`
          : `${dayLbl} · ${role}`;
      }
    }
    return { allLegSeries: seriesMap, legLabels: labels };
  }, [obsData]);

  if (!kindObs.length) return null;

  // Most recent obs drives the card header
  const latestObs  = obsData[0]?.obs ?? kindObs[0];
  const activeId   = kindObs.find(o => o.status === 'active')?.id ?? null;
  const liveData   = activeId ? liveDataMap[activeId] : null;
  const marketLive = liveData?.market_live === true;

  // Latest real sample across all obs (for health lines / next-sample)
  const latestRealRow = obsData.flatMap(({ samples }) => samples?.rows || [])
    .filter(r => !r.gap_reason && r.ltp != null && r.role_label !== 'spot')
    .sort((a, b) => b.captured_at > a.captured_at ? 1 : -1)[0] ?? null;
  const lastReadingAt = latestRealRow?.captured_at ?? null;
  const activeObsMeta = kindObs.find(o => o.status === 'active') ?? null;
  const nextSample    = computeNextSample(deskStatus, lastReadingAt, activeObsMeta?.interval_seconds);

  // Header value: last-known total from the most recent obs (live if active + mkt open)
  const latestRows = obsData[0]?.samples?.rows || [];
  const latestRoles = [...new Set(latestRows.filter(r => r.role_label !== 'spot').map(r => r.role_label))];
  let headerTotal = null;
  if (marketLive && liveData?.total_premium != null) {
    headerTotal = liveData.total_premium;
  } else if (latestRoles.length) {
    // Sum last real sample per leg — null if any leg is absent (no partial sums)
    let sum = 0, anyMissing = false;
    for (const role of latestRoles) {
      const v = legLastReal(latestRows, role)?.ltp ?? null;
      if (v == null) { anyMissing = true; break; }
      sum += v;
    }
    headerTotal = anyMissing ? null : sum;
  }

  const headerValue = headerTotal != null
    ? <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-data, monospace)', color: NIFTY_CHART_COLORS[0] }}>{headerTotal.toFixed(2)}</span>
    : <span style={{ color: 'var(--ink-4)', fontSize: 11 }}>—</span>;

  return (
    <ObservationCard
      observation={latestObs}
      deskStatus={deskStatus}
      lastReadingAt={lastReadingAt}
      nextSample={nextSample}
      live={null}
      headerValue={headerValue}
    >
      <div style={{ padding: '12px 14px 16px' }}>
        {/* Chart above table so the table "drives" it — user ticks checkboxes and sees chart update */}
        {selectedLegs.size > 0 && (
          <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--r-3)', padding: '6px 4px 2px', marginBottom: 8 }}>
            <NiftyPerLegChart
              allLegSeries={allLegSeries}
              selectedLegs={selectedLegs}
              colorMap={colorMap}
              legLabels={legLabels}
            />
          </div>
        )}

        {selectedLegs.size === 0 && (
          <div style={{
            padding: '18px', textAlign: 'center', fontSize: 11, color: 'var(--ink-4)',
            background: 'var(--surface-2)', borderRadius: 'var(--r-3)', marginBottom: 8,
          }}>
            Expand a day below and tick legs to plot
          </div>
        )}

        <div style={{ fontSize: 9, color: 'var(--ink-4)', marginBottom: 8 }}>
          X = IST time-of-day (09:00–15:35) · gaps = socket down / market closed · days stack on same axis
        </div>

        <NiftyDayTable
          obsDataList={obsData}
          selectedLegs={selectedLegs}
          onToggleLeg={toggleLeg}
          colorMap={colorMap}
          liveDataMap={liveDataMap}
          marketLiveId={marketLive ? activeId : null}
        />
      </div>
    </ObservationCard>
  );
}

// ── Straddle components ───────────────────────────────────────────────────────
// Role labels from the backend: "straddle CE" / "straddle PE"
function parseStraddleRole(label) {
  if (!label) return null;
  const m = label.match(/^straddle (CE|PE)$/);
  if (!m) return null;
  return { optType: m[1], isCall: m[1] === 'CE', isPut: m[1] === 'PE' };
}

// Straddle day table: one row per obs (day), showing combined lock / close / Δ / %
function StraddleDayTable({ obsDataList, liveDataMap, marketLiveId }) {
  const [openDays, setOpenDays] = useState(new Set());
  const toggleDay = (id) => setOpenDays(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  if (!obsDataList.length) return null;

  return (
    <div>
      {obsDataList.map(({ obs, samples, instruments }) => {
        const rows    = samples?.rows || [];
        const insts   = instruments?.instruments || [];
        const ceInst  = insts.find(i => i.role_label === 'straddle CE');
        const peInst  = insts.find(i => i.role_label === 'straddle PE');
        const isOpen  = openDays.has(obs.id);
        const isActive = obs.status === 'active';
        const liveData = liveDataMap[obs.id] ?? null;
        const mktLive  = isActive && marketLiveId === obs.id && liveData?.market_live === true;

        const dayLabel = istDayLabel(obs.created_at);
        const lockTime = istTimeHHMM(obs.created_at);

        // Spot move
        const spotRows  = rows.filter(r => r.role_label === 'spot' && !r.gap_reason && r.ltp != null)
                              .sort((a, b) => a.captured_at < b.captured_at ? -1 : 1);
        const spotOpen  = spotRows[0]?.ltp ?? null;
        const spotClose = mktLive ? (liveData?.spot?.ltp ?? spotRows[spotRows.length - 1]?.ltp ?? null)
                                  : (spotRows[spotRows.length - 1]?.ltp ?? null);
        const spotMove  = spotOpen != null && spotClose != null ? spotClose - spotOpen : null;
        const fmtSpot   = (v) => v != null ? Math.round(v).toLocaleString('en-IN') : '—';
        const moveColor = spotMove == null ? 'var(--ink-4)' : spotMove >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
        const signedMove = spotMove != null
          ? `${spotMove >= 0 ? '+' : ''}${Math.round(spotMove).toLocaleString('en-IN')}`
          : null;

        // Leg values
        const ceLock = ceInst?.ltp_at_lock ?? null;
        const peLock = peInst?.ltp_at_lock ?? null;
        const totalLock = ceLock != null && peLock != null ? ceLock + peLock : null;

        const ceLiveleg  = mktLive ? liveData?.legs?.find(l => l.role_label === 'straddle CE') : null;
        const peLiveleg  = mktLive ? liveData?.legs?.find(l => l.role_label === 'straddle PE') : null;
        const ceCloseRow = legLastReal(rows, 'straddle CE');
        const peCloseRow = legLastReal(rows, 'straddle PE');
        const ceClose = mktLive && ceLiveleg?.ltp_now != null ? ceLiveleg.ltp_now : (ceCloseRow?.ltp ?? null);
        const peClose = mktLive && peLiveleg?.ltp_now != null ? peLiveleg.ltp_now : (peCloseRow?.ltp ?? null);
        const totalClose = ceClose != null && peClose != null ? ceClose + peClose : null;

        const totalDelta  = totalLock != null && totalClose != null ? totalClose - totalLock : null;
        const totalPct    = totalDelta != null && totalLock > 0 ? (totalDelta / totalLock) * 100 : null;

        const dColor = (d) => d == null ? 'var(--ink-4)' : d < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
        const fmt2n  = (v) => v != null ? Number(v).toFixed(2) : '—';
        const fmtPct = (v) => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}%` : '—';

        return (
          <div key={obs.id} style={{
            border: '.5px solid var(--hair)', borderRadius: 'var(--r-3)',
            marginBottom: 6, overflow: 'hidden',
          }}>
            <div
              role="button" tabIndex={0}
              onClick={() => toggleDay(obs.id)}
              onKeyDown={e => e.key === 'Enter' && toggleDay(obs.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', cursor: 'pointer',
                background: isOpen ? 'var(--surface-2)' : 'var(--surface-1)',
                userSelect: 'none',
              }}
            >
              <span style={{
                fontSize: 10, color: 'var(--ink-3)',
                transform: isOpen ? 'rotate(90deg)' : 'none',
                display: 'inline-block', transition: 'transform .12s', flexShrink: 0,
              }}>▸</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? 'var(--up-field-ink)' : 'var(--ink-2)', flexShrink: 0 }}>
                {dayLabel}
              </span>
              <span style={{ fontSize: 10, color: 'var(--ink-4)', flexShrink: 0 }}>
                locked {lockTime}
              </span>
              <span style={{ fontSize: 10, color: 'var(--ink-3)', flexShrink: 0 }}>
                spot{' '}
                <span style={{ fontFamily: 'var(--font-data, monospace)' }}>{fmtSpot(spotOpen)}</span>
                {' → '}
                <span style={{ fontFamily: 'var(--font-data, monospace)' }}>{fmtSpot(spotClose)}</span>
                {signedMove != null && (
                  <span style={{ color: moveColor, fontFamily: 'var(--font-data, monospace)', marginLeft: 4 }}>
                    ({signedMove})
                  </span>
                )}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ink-4)', flexShrink: 0, fontFamily: 'var(--font-data, monospace)' }}>
                {totalLock != null ? `lock ${fmt2n(totalLock)}` : ''}
              </span>
            </div>

            {isOpen && (
              <div style={{ borderTop: '.5px solid var(--hair)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2)' }}>
                      {[
                        { h: 'Leg',   right: false },
                        { h: 'Lock',  right: true  },
                        { h: mktLive ? 'Now' : 'Close', right: true },
                        { h: 'Δ',     right: true  },
                        { h: '%',     right: true  },
                      ].map(({ h, right }) => (
                        <th key={h} style={{
                          fontSize: 9.5, fontWeight: 700, color: 'var(--ink-3)',
                          textTransform: 'uppercase', letterSpacing: '.06em',
                          padding: '4px 8px', textAlign: right ? 'right' : 'left',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Total row */}
                    <tr style={{ borderTop: '.5px solid var(--hair)', fontWeight: 700 }}>
                      <td style={{ padding: '4px 8px', color: 'var(--ink-2)' }}>Total straddle</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>{fmt2n(totalLock)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)' }}>{fmt2n(totalClose)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: dColor(totalDelta) }}>
                        {totalDelta != null ? (totalDelta >= 0 ? '+' : '') + totalDelta.toFixed(2) : '—'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: dColor(totalPct) }}>{fmtPct(totalPct)}</td>
                    </tr>
                    {/* CE leg */}
                    {['CE', 'PE'].map(optType => {
                      const inst     = optType === 'CE' ? ceInst : peInst;
                      const lock     = optType === 'CE' ? ceLock : peLock;
                      const closeVal = optType === 'CE' ? ceClose : peClose;
                      const d        = lock != null && closeVal != null ? closeVal - lock : null;
                      const pct      = d != null && lock > 0 ? (d / lock) * 100 : null;
                      return (
                        <tr key={optType} style={{ borderTop: '.5px solid var(--hair)', opacity: 0.8 }}>
                          <td style={{ padding: '4px 8px 4px 20px', color: 'var(--ink-3)', fontSize: 10.5 }}>
                            └ {optType}{inst?.strike != null ? ` ${Math.round(inst.strike)}` : ''}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>{fmt2n(lock)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: 'var(--ink-3)' }}>{fmt2n(closeVal)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: dColor(d) }}>
                            {d != null ? (d >= 0 ? '+' : '') + d.toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-data, monospace)', color: dColor(pct) }}>{fmtPct(pct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// StraddleKindCard — one card for nifty_straddle or sensex_straddle
const STRADDLE_SESSION_START = 9 * 3600 + 20 * 60;
const STRADDLE_SESSION_END   = 15 * 3600 + 25 * 60;
const STRADDLE_COLOR_CE = '#2a78d6';
const STRADDLE_COLOR_PE = '#e07b2a';

function StraddleKindCard({ kind, obsList, deskStatus }) {
  const kindObs = useMemo(
    () => (obsList || []).filter(o => o.kind === kind),
    [obsList, kind]
  );

  const [obsData,     setObsData]     = useState([]);
  const [liveDataMap, setLiveDataMap] = useState({});

  useEffect(() => {
    if (!kindObs.length) return;
    let cancelled = false;
    const loadAll = async () => {
      const results = await Promise.allSettled(
        kindObs.map(obs =>
          Promise.allSettled([
            getObservationSamples(obs.id, 1000),
            getObservationInstruments(obs.id),
          ]).then(([sRes, iRes]) => ({
            obs,
            samples:     sRes.status === 'fulfilled' ? sRes.value : null,
            instruments: iRes.status === 'fulfilled' ? iRes.value : null,
          }))
        )
      );
      if (!cancelled) {
        setObsData(results.filter(r => r.status === 'fulfilled').map(r => r.value));
      }
    };
    loadAll();
    const t = setInterval(loadAll, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [kindObs]);

  useEffect(() => {
    const activeObs = kindObs.filter(o => o.status === 'active');
    if (!activeObs.length) return;
    let cancelled = false;
    const loadLive = async () => {
      const results = await Promise.allSettled(activeObs.map(o => getNiftyObsLive(o.id)));
      if (!cancelled) {
        const map = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value) map[activeObs[i].id] = r.value;
        });
        setLiveDataMap(map);
      }
    };
    loadLive();
    const t = setInterval(loadLive, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [kindObs]);

  // All hooks before any conditional return
  const activeId = kindObs.find(o => o.status === 'active')?.id ?? null;
  const activeObsId = activeId ?? kindObs[0]?.id;

  const allLegSeries = useMemo(() => {
    const map = new Map();
    for (const { obs, samples } of obsData) {
      const rows = samples?.rows || [];
      for (const optType of ['CE', 'PE']) {
        const role = `straddle ${optType}`;
        map.set(`${obs.id}:${role}`, buildLegSeries(rows, role));
      }
    }
    return map;
  }, [obsData]);

  const colorMap = useMemo(() => {
    const m = {};
    for (const key of allLegSeries.keys()) {
      m[key] = key.includes(':straddle CE') ? STRADDLE_COLOR_CE : STRADDLE_COLOR_PE;
    }
    return m;
  }, [allLegSeries]);

  const defaultChartLegs = useMemo(() => {
    const s = new Set();
    if (activeObsId) {
      s.add(`${activeObsId}:straddle CE`);
      s.add(`${activeObsId}:straddle PE`);
    }
    return s;
  }, [activeObsId]);

  const [chartLegsOverride, setChartLegsOverride] = useState(null);
  const effectiveLegs = chartLegsOverride ?? defaultChartLegs;

  const straddleChartNode = useMemo(() => {
    const PAD = { t: 10, r: 14, b: 28, l: 42 };
    const VW = 600, VH = 195;
    const innerW = VW - PAD.l - PAD.r;
    const innerH = VH - PAD.t - PAD.b;

    const allNonNull = [];
    for (const key of effectiveLegs) {
      for (const p of (allLegSeries.get(key) || [])) {
        if (p.v != null) allNonNull.push({ ...p, key });
      }
    }

    if (!allNonNull.length) return (
      <div style={{ height: VH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)', fontSize: 11 }}>
        No data yet
      </div>
    );

    const xOf = (isoStr) => {
      const sec  = istSecOfDay(utcDate(isoStr).getTime());
      const frac = Math.max(0, Math.min(1, (sec - STRADDLE_SESSION_START) / (STRADDLE_SESSION_END - STRADDLE_SESSION_START)));
      return PAD.l + frac * innerW;
    };
    const vMax = Math.max(...allNonNull.map(p => p.v)) * 1.18 || 1;
    const yOf  = (v) => PAD.t + (1 - v / vMax) * innerH;
    const gridVals = [0, 0.25, 0.5, 0.75, 1].map(f => f * vMax);

    const xLabels = [];
    for (let h = 9; h <= 15; h++) {
      const sec  = h * 3600 + (h === 9 ? 20 * 60 : 0);
      const frac = (sec - STRADDLE_SESSION_START) / (STRADDLE_SESSION_END - STRADDLE_SESSION_START);
      if (frac >= 0 && frac <= 1) {
        xLabels.push({ x: PAD.l + frac * innerW, label: `${String(h).padStart(2,'0')}:${h === 9 ? '20' : '00'}` });
      }
    }

    return (
      <svg viewBox={`0 0 ${VW} ${VH}`} style={{ width: '100%', height: VH, display: 'block' }} preserveAspectRatio="none">
        {gridVals.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={yOf(v)} x2={VW - PAD.r} y2={yOf(v)} stroke="var(--hair)" strokeWidth={0.75}/>
            <text x={PAD.l - 6} y={yOf(v) + 3.5} textAnchor="end" fontSize={9} fill="var(--ink-3)">{v.toFixed(0)}</text>
          </g>
        ))}
        <line x1={PAD.l} y1={VH - PAD.b} x2={VW - PAD.r} y2={VH - PAD.b} stroke="var(--hair-strong)" strokeWidth={0.75}/>
        {xLabels.map(({ x, label }) => (
          <text key={label} x={x} y={VH - PAD.b + 13} textAnchor="middle" fontSize={9} fill="var(--ink-3)">{label}</text>
        ))}
        {[...effectiveLegs].map(key => {
          const series = allLegSeries.get(key) || [];
          const color  = colorMap[key] || '#888';
          return (
            <g key={key}>
              {toSegments(series).map((seg, i) => {
                if (seg.length === 1) return <circle key={i} cx={xOf(seg[0].t)} cy={yOf(seg[0].v)} r={2.5} fill={color}/>;
                const pts = seg.map(p => `${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' ');
                return <polyline key={i} points={pts} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round"/>;
              })}
            </g>
          );
        })}
      </svg>
    );
  }, [allLegSeries, effectiveLegs, colorMap]);

  // Safe early return — all hooks above
  if (!kindObs.length) return null;

  const liveData   = activeId ? liveDataMap[activeId] : null;
  const marketLive = liveData?.market_live === true;

  const latestRealRow = obsData.flatMap(({ samples }) => samples?.rows || [])
    .filter(r => !r.gap_reason && r.ltp != null && r.role_label !== 'spot')
    .sort((a, b) => b.captured_at > a.captured_at ? 1 : -1)[0] ?? null;
  const lastReadingAt = latestRealRow?.captured_at ?? null;
  const activeObsMeta = kindObs.find(o => o.status === 'active') ?? null;
  const nextSample    = computeNextSample(deskStatus, lastReadingAt, activeObsMeta?.interval_seconds);

  const latestRows = obsData[0]?.samples?.rows || [];
  let headerTotal  = null;
  if (marketLive && liveData?.total_premium != null) {
    headerTotal = liveData.total_premium;
  } else {
    const ceReal = legLastReal(latestRows, 'straddle CE');
    const peReal = legLastReal(latestRows, 'straddle PE');
    if (ceReal?.ltp != null && peReal?.ltp != null) headerTotal = ceReal.ltp + peReal.ltp;
  }

  const headerValue = headerTotal != null
    ? <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-data, monospace)', color: STRADDLE_COLOR_CE }}>{headerTotal.toFixed(2)}</span>
    : <span style={{ color: 'var(--ink-4)', fontSize: 11 }}>—</span>;

  const latestObs = obsData[0]?.obs ?? kindObs[0];

  return (
    <ObservationCard
      observation={latestObs}
      deskStatus={deskStatus}
      lastReadingAt={lastReadingAt}
      nextSample={nextSample}
      live={null}
      headerValue={headerValue}
    >
      <div style={{ padding: '12px 14px 16px' }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 7 }}>
          {[['CE', STRADDLE_COLOR_CE], ['PE', STRADDLE_COLOR_PE]].map(([label, color]) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: 'var(--ink-2)' }}>
              <span style={{ display: 'inline-block', width: 18, height: 2, background: color, borderRadius: 1 }}/>
              {label}
            </span>
          ))}
        </div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--r-3)', padding: '6px 4px 2px', marginBottom: 4 }}>
          {straddleChartNode}
        </div>
        <div style={{ fontSize: 9, color: 'var(--ink-4)', marginBottom: 8 }}>
          X = IST 09:20–15:25 · gaps = socket down / market closed
        </div>
        <StraddleDayTable
          obsDataList={obsData}
          liveDataMap={liveDataMap}
          marketLiveId={marketLive ? activeId : null}
        />
      </div>
    </ObservationCard>
  );
}

// ── NiftyObsSection ──────────────────────────────────────────────────────────
// Fetches the observation list once and passes it into two NiftyKindCards.
function NiftyObsSection({ deskStatus }) {
  const [obsList, setObsList] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getObservationList('all').then(data => {
      if (!cancelled) setObsList(data?.observations || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!obsList) return null;

  const hasDecay          = obsList.some(o => o.kind === 'nifty_decay');
  const hasDelta          = obsList.some(o => o.kind === 'nifty_delta_decay');
  const hasNiftyStraddle  = obsList.some(o => o.kind === 'nifty_straddle');
  const hasSensexStraddle = obsList.some(o => o.kind === 'sensex_straddle');

  if (!hasDecay && !hasDelta && !hasNiftyStraddle && !hasSensexStraddle) return null;

  return (
    <>
      {hasDecay          && <NiftyKindCard    kind="nifty_decay"       obsList={obsList} deskStatus={deskStatus}/>}
      {hasDelta          && <NiftyKindCard    kind="nifty_delta_decay" obsList={obsList} deskStatus={deskStatus}/>}
      {hasNiftyStraddle  && <StraddleKindCard kind="nifty_straddle"    obsList={obsList} deskStatus={deskStatus}/>}
      {hasSensexStraddle && <StraddleKindCard kind="sensex_straddle"   obsList={obsList} deskStatus={deskStatus}/>}
    </>
  );
}

// ── DeskObservationsTab ───────────────────────────────────────────────────────
function DeskObservationsTab({ deskStatus }) {
  const [samples,     setSamples]     = useState(null);
  const [instruments, setInstruments] = useState(null);
  const [liveData,    setLiveData]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [fetchErr,    setFetchErr]    = useState(null);

  const load = useCallback(async () => {
    try {
      setFetchErr(null);
      // Fetch samples, instruments, and live tick snapshot in parallel.
      // instruments and live failures are non-fatal.
      const [samplesData, instData, liveResp] = await Promise.allSettled([
        getObservationSamples(NG_OBS_ID, 500),
        getObservationInstruments(NG_OBS_ID),
        getObservationLive(NG_OBS_ID),
      ]);
      if (samplesData.status === 'fulfilled') setSamples(samplesData.value);
      else throw samplesData.reason;
      if (instData.status === 'fulfilled')  setInstruments(instData.value);
      if (liveResp.status  === 'fulfilled') setLiveData(liveResp.value);
      // instruments/live failures non-fatal: table shows "—", pulse shows nothing
    } catch (e) {
      setFetchErr(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh every 30 s
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !samples) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--ink-3)', fontSize: 11 }}>
        Loading…
      </div>
    );
  }

  if (fetchErr) {
    return (
      <div style={{
        padding: '12px 14px', fontSize: 11,
        color: 'var(--down-field-ink)', background: 'var(--surface-2)',
        borderRadius: 'var(--r-3)', border: '.5px solid var(--hair)',
      }}>
        Error loading observation: {fetchErr}
      </div>
    );
  }

  const obs = samples?.observation;
  if (!obs) {
    return (
      <div style={{
        padding: '14px', fontSize: 11, color: 'var(--ink-3)',
        background: 'var(--surface-2)', borderRadius: 'var(--r-3)',
        border: '.5px solid var(--hair)',
      }}>
        Observation id={NG_OBS_ID} not found.
        Backend needs: <code>GET /api/desk/observations</code>
      </div>
    );
  }

  const rows = samples?.rows || [];

  // Compute summary line for triage row — NG-specific knowledge lives HERE, not in the container.
  const { series, maxSetNum } = buildPremiumSeries(rows);
  const activeStraddle = series[maxSetNum]?.straddle || [];
  const activeStrangle = series[maxSetNum]?.strangle || [];
  const latestStPt = [...activeStraddle].reverse().find(p => p.v != null);
  const latestSgPt = [...activeStrangle].reverse().find(p => p.v != null);

  const summaryLine = latestStPt && latestSgPt
    ? `straddle ${fmt2(latestStPt.v)} · strangle ${fmt2(latestSgPt.v)}`
    : rows.length ? 'awaiting live tick' : 'no samples yet';

  // Latest real sample timestamp (non-gap rows are unordered; newest-first from API)
  const latestRealRow = rows.find(r => !r.gap_reason && r.ltp != null);
  const lastReadingAt = latestRealRow?.captured_at ?? null;

  // Countdown uses last REAL sample — agrees with dual-timer: gap fires don't advance the real schedule
  const nextSample = computeNextSample(deskStatus, lastReadingAt, obs?.interval_seconds);

  return (
    <div>
      {/*
        ObservationCard is inner-agnostic — it renders {children}.
        NgDecayInner is composed HERE, not imported by the container.
      */}
      <ObservationCard
        observation={obs}
        deskStatus={deskStatus}
        lastReadingAt={lastReadingAt}
        nextSample={nextSample}
        live={liveData}
      >
        {/* SLOT: NgDecayInner plugs in as children */}
        <NgDecayInner samples={samples} instruments={instruments} live={liveData}/>
      </ObservationCard>

      {/* NIFTY observation cards — one for nifty_decay, one for nifty_delta_decay */}
      <NiftyObsSection deskStatus={deskStatus}/>
    </div>
  );
}

// ── NgCandlesTab ──────────────────────────────────────────────────────────────

function NgCandlesTab() {
  const [ivSel,    setIvSel]   = useState('1h');
  const [data,     setData]    = useState(null);
  const [indData,  setIndData] = useState(null);
  const [error,    setError]   = useState(null);
  const [loading,  setLoading] = useState(false);

  const fetchBars = async (iv) => {
    if (iv === 'indicators') {
      setLoading(true); setError(null);
      try { setIndData(await getNgIndicators(30)); }
      catch (e) { setError(e.message || String(e)); }
      finally { setLoading(false); }
      return;
    }
    setLoading(true); setError(null);
    try { setData(await getNgCandles(iv, 50)); }
    catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBars(ivSel); }, [ivSel]);

  const st   = data?.status;
  const bars = data?.bars || [];

  const col = { padding: '4px 8px', textAlign: 'right', fontSize: 11, fontVariantNumeric: 'tabular-nums' };
  const hdr = { ...col, color: 'var(--ink-3)', fontWeight: 600, borderBottom: '1px solid var(--line)' };
  const rowBorder = { borderBottom: '1px solid var(--line)' };

  return (
    <div style={{ padding: '0 16px 32px' }}>
      {/* Mode selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {[
          { key: '1h',         label: '1h bars'    },
          { key: 'day',        label: 'Daily bars'  },
          { key: 'indicators', label: 'Indicators'  },
        ].map(({ key, label }) => (
          <button key={key} className={`stab${ivSel === key ? ' on' : ''}`}
            style={{ fontSize: 11 }} onClick={() => setIvSel(key)}>{label}</button>
        ))}
        <button style={{ marginLeft: 4, fontSize: 11, padding: '2px 8px' }}
          onClick={() => fetchBars(ivSel)}>↺</button>
        {loading && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>loading…</span>}
      </div>

      {/* Engine status strip (bars mode only) */}
      {st && ivSel !== 'indicators' && (
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11,
          color: 'var(--ink-3)', marginBottom: 10,
          padding: '6px 10px', background: 'var(--well)', borderRadius: 6,
        }}>
          <span>token <b style={{ color: 'var(--ink-1)' }}>{st.token ?? '—'}</b></span>
          <span>hist <b style={{ color: 'var(--ink-1)' }}>{st.hist_bars}</b></span>
          <span>tick <b style={{ color: 'var(--ink-1)' }}>{st.tick_bars}</b></span>
          <span>forming <b style={{ color: st.forming_active ? 'var(--green)' : 'var(--ink-3)' }}>
            {st.forming_active ? 'yes' : 'no'}</b></span>
          <span style={{ color: st.kama_1500_shortfall > 0 ? 'var(--orange)' : 'var(--green)' }}>
            KAMA(1500): {st.kama_1500_available}/{st.kama_1500_needed}
            {st.kama_1500_shortfall > 0 && ` (−${st.kama_1500_shortfall})`}
          </span>
        </div>
      )}

      {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</div>}

      {/* ── Indicators view ── */}
      {ivSel === 'indicators' && indData && (() => {
        const kama = indData.kama_daily || [];
        const ema3 = indData.ema3_1h    || [];
        return (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>

            {/* Daily KAMA(21,2,30) */}
            <div style={{ flex: '1 1 300px', overflowX: 'auto' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
                Daily KAMA(21,2,30) — last {kama.length} days
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                <thead><tr>
                  <th style={{ ...hdr, textAlign: 'left' }}>Date</th>
                  <th style={hdr}>Daily close</th>
                  <th style={hdr}>KAMA</th>
                </tr></thead>
                <tbody>
                  {[...kama].reverse().map((r, i) => (
                    <tr key={i} style={{
                      ...rowBorder,
                      background: r.forming ? 'rgba(255,200,0,0.08)' : undefined,
                    }}>
                      <td style={{ ...col, textAlign: 'left', fontFamily: 'monospace' }}>
                        {r.date}{r.forming && <span style={{ color: 'var(--orange)', marginLeft: 3 }}>●</span>}
                      </td>
                      <td style={col}>{r.close?.toFixed(2)}</td>
                      <td style={{ ...col, fontWeight: 600 }}>{r.kama?.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 1h EMA(3) */}
            <div style={{ flex: '1 1 340px', overflowX: 'auto' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
                1h EMA(3) — last {ema3.length} bars
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                <thead><tr>
                  <th style={{ ...hdr, textAlign: 'left' }}>Timestamp</th>
                  <th style={hdr}>1h close</th>
                  <th style={hdr}>EMA(3)</th>
                </tr></thead>
                <tbody>
                  {[...ema3].reverse().map((r, i) => (
                    <tr key={i} style={{
                      ...rowBorder,
                      background: r.forming ? 'rgba(255,200,0,0.08)' : undefined,
                    }}>
                      <td style={{ ...col, textAlign: 'left', fontFamily: 'monospace' }}>
                        {r.timestamp}{r.forming && <span style={{ color: 'var(--orange)', marginLeft: 3 }}>●</span>}
                      </td>
                      <td style={col}>{r.close?.toFixed(2)}</td>
                      <td style={{ ...col, fontWeight: 600 }}>{r.ema3?.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        );
      })()}

      {ivSel === 'indicators' && !indData && !loading && !error && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
          No indicator data — engine may be warming up.
        </div>
      )}

      {/* ── Bars view (1h / day) ── */}
      {ivSel !== 'indicators' && bars.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ ...hdr, textAlign: 'left' }}>{ivSel === '1h' ? 'Timestamp' : 'Date'}</th>
                <th style={hdr}>Open</th>
                <th style={hdr}>High</th>
                <th style={hdr}>Low</th>
                <th style={hdr}>Close</th>
                <th style={hdr}>Volume</th>
                {ivSel === '1h'  && <th style={hdr}>Source</th>}
                {ivSel === 'day' && <th style={hdr}>Bars</th>}
              </tr>
            </thead>
            <tbody>
              {[...bars].reverse().map((b, i) => (
                <tr key={i} style={{
                  background: b.forming ? 'rgba(255,200,0,0.08)' : undefined,
                  ...rowBorder,
                }}>
                  <td style={{ ...col, textAlign: 'left', fontFamily: 'monospace' }}>
                    {ivSel === '1h' ? b.timestamp : b.date}
                    {b.forming && <span style={{ color: 'var(--orange)', marginLeft: 4 }}>●</span>}
                  </td>
                  <td style={col}>{b.open?.toFixed(2)}</td>
                  <td style={{ ...col, color: 'var(--green)' }}>{b.high?.toFixed(2)}</td>
                  <td style={{ ...col, color: 'var(--red)'   }}>{b.low?.toFixed(2)}</td>
                  <td style={{ ...col, fontWeight: 600 }}>{b.close?.toFixed(2)}</td>
                  <td style={col}>{b.volume?.toLocaleString()}</td>
                  {ivSel === '1h'  && <td style={{ ...col, color: 'var(--ink-3)' }}>{b.source}</td>}
                  {ivSel === 'day' && <td style={{ ...col, color: 'var(--ink-3)' }}>{b.bars_1h}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ivSel !== 'indicators' && !loading && bars.length === 0 && !error && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
          No bars loaded — engine may be warming up or Kite not connected.
        </div>
      )}
    </div>
  );
}

// ── DeskTab — top-level tab ───────────────────────────────────────────────────
export default function DeskTab({ initSub = 'observations', onSubChange }) {
  const [sub, setSub]             = useState(initSub || 'observations');
  const [deskStatus, setDeskStatus] = useState(null);

  // Sync when sidebar navigation changes the active sub while tab is already mounted
  useEffect(() => {
    if (initSub) setSub(initSub);
  }, [initSub]);

  // Poll /api/desk/status every 15 s
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await getDeskStatus();
        if (!cancelled) setDeskStatus(data?.state || 'NOT_CONNECTED');
      } catch {
        if (!cancelled) setDeskStatus('DISCONNECTED');
      }
    };
    poll();
    const t = setInterval(poll, 15_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const goSub = (s) => {
    setSub(s);
    onSubChange?.(s);
  };

  return (
    <div>
      {/* Sub-tab bar */}
      <div className="subtabs" style={{
        position: 'sticky', top: 44, zIndex: 10,
        background: 'var(--paper)',
        display: 'flex', alignItems: 'center',
      }}>
        {[
          { key: 'observations', label: 'Observations'  },
          { key: 'candles',      label: 'NG Candles'    },
          { key: 'paper',        label: 'Paper Trading' },
          { key: 'live',         label: 'Live Trading'  },
          { key: 'upstox',       label: 'Positions'     },
        ].map(({ key, label }) => (
          <button key={key}
            className={`stab${sub === key ? ' on' : ''}`}
            onClick={() => goSub(key)}>
            {label}
          </button>
        ))}

        {/* Global state chip — right-aligned in the bar */}
        {deskStatus && (
          <div style={{ marginLeft: 'auto', paddingRight: 14, display: 'flex', alignItems: 'center' }}>
            <StateChip state={deskStatus}/>
          </div>
        )}
      </div>

      <div style={{ paddingTop: 12 }}>
        {sub === 'observations' && (
          <DeskObservationsTab deskStatus={deskStatus}/>
        )}
        {sub === 'candles' && <NgCandlesTab />}
        {sub === 'paper' && <PaperTradingTab />}
        {sub === 'live' && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
            Live Trading — not yet built
          </div>
        )}
        {sub === 'upstox' && <UpstoxTab />}
      </div>
    </div>
  );
}
